import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { usePermissions } from '../hooks/usePermissions';
import {
    GraduationCap,
    Clock,
    Users,
    Calendar,
    Plus,
    Search,
    Filter,
    Eye,
    Edit2,
    Trash2,
    MapPin,
    BookOpen,
    DollarSign,
    MoreHorizontal,
    Star,
    TrendingUp,
    AlertCircle,
    X,
    Settings,
    Phone,
    Mail,
    Globe,
    Activity,
    Upload,
    Download,
    FileSpreadsheet,
    Link,
    CheckCircle,
    XCircle,
    AlertTriangle,
    FileText,
    Award,
    Target
} from 'lucide-react';
import { classesApi, campusApi, subjectsApi, studentsApi, usersApi, api } from '../services/api';
import {
    ActionMenu,
    Button,
    DataToolbar,
    DetailFooter,
    DetailHeader,
    Input,
    LocalPagination,
    Modal,
    ResponsiveDataView,
    Select,
    StatCard,
    StatusBadge,
    statGroupClassName,
    Textarea,
    useLocalPagination,
    useToast,
    DateInput,
    SearchSelect,
} from '../components/ui';
import { personPickerOptions } from '../utils/pickerOptions';
import { foldText, matchesQuery } from '../utils/search';

// Name, email or phone, ignoring case and accents.
const matchesStudentSearch = (student: any, term: string) =>
    matchesQuery([student.name, student.email, student.phone, student.studentId].filter(Boolean).join(' '), term);
import { PageHeader } from '../components/PageHeader';
import { openOnRowClick } from '../utils/rowClick';
import { ClassGradebook } from '../components/classes/ClassGradebook';
import { ClassShiftType } from '../components/classes/ClassShiftType';
import { useAuth } from '../contexts/AuthContext';

const CLASSES_PAGE_SIZE = 10;

// Class staff come from the class relations; the free-text field is the legacy fallback.
const staffDisplayName = (person: any, legacy?: string | null) =>
    `${person?.firstName ?? ''} ${person?.lastName ?? ''}`.trim() || person?.name || legacy || '';

function ClassFact({ label, value, hint, wide }: { label: string; value: React.ReactNode; hint?: string; wide?: boolean }) {
    return (
        <div className={wide ? 'col-span-2 lg:col-span-4' : undefined}>
            <dt className="text-xs text-ink-500">{label}</dt>
            <dd className="mt-0.5 font-medium text-ink-900 break-words">{value || <span className="font-normal text-ink-400">Chưa có</span>}</dd>
            {hint ? <dd className="text-xs text-ink-500 break-words">{hint}</dd> : null}
        </div>
    );
}

const Classes = () => {
    const { hasPermission, hasAnyPermission, loading: permissionsLoading, permissions } = usePermissions();
    const { user } = useAuth();
    const { showToast: showUiToast } = useToast();
    const [searchTerm, setSearchTerm] = useState('');

    // Debug permissions loading
    useEffect(() => {
        // console.log('Permissions status:', { permissionsLoading, permissions: !!permissions });
    }, [permissionsLoading, permissions]);
    const [selectedCampus, setSelectedCampus] = useState('all');
    const [selectedStatus, setSelectedStatus] = useState('all');
    const [showForm, setShowForm] = useState(false);

    // New state for comprehensive functionality
    const [openDropdown, setOpenDropdown] = useState<number | null>(null);
    const [showViewModal, setShowViewModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showSettingsModal, setShowSettingsModal] = useState(false);
    const [showStudentsModal, setShowStudentsModal] = useState(false);
    const [selectedClass, setSelectedClass] = useState<any>(null);
    const [classes, setClasses] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [campusOptions, setCampusOptions] = useState<any[]>([]);
    const [subjectOptions, setSubjectOptions] = useState<any[]>([]);
    const [canAccessCampus, setCanAccessCampus] = useState(false);

    // Helper function to get campus display name
    const getCampusDisplayName = (campusData: any) => {
        if (!campusData) return 'Không xác định';

        // If campus is an object with name property
        if (typeof campusData === 'object' && campusData.name) {
            return campusData.name;
        }

        // If campus is an ID and we have campus options, lookup the name
        if (canAccessCampus && campusOptions.length > 0) {
            const campus = campusOptions.find(c => c.id === campusData);
            if (campus) return campus.name;
        }

        // If we don't have campus access, show a generic message
        if (!canAccessCampus) {
            return 'Cơ sở được phân công';
        }

        // Fallback - show the raw value or unknown
        return campusData || 'Không xác định';
    };

    // Student management state
    const [classStudents, setClassStudents] = useState<any[]>([]);
    const [allStudents, setAllStudents] = useState<any[]>([]);
    const [studentsLoading, setStudentsLoading] = useState(false);
    const [showAddStudentModal, setShowAddStudentModal] = useState(false);
    const [selectedStudentIds, setSelectedStudentIds] = useState<string[]>([]);
    const [isEnrollingMultiple, setIsEnrollingMultiple] = useState(false);

    // Student search and import state
    const [studentSearchTerm, setStudentSearchTerm] = useState('');
    const [foundStudentsFromPaste, setFoundStudentsFromPaste] = useState<any[]>([]);
    const [showPasteInstructions, setShowPasteInstructions] = useState(false);
    const [showStudentImportModal, setShowStudentImportModal] = useState(false);
    const [studentImportMethod, setStudentImportMethod] = useState<'file' | 'googlesheets'>('file');
    const [studentImportFile, setStudentImportFile] = useState<File | null>(null);
    const [studentImportGoogleSheetsUrl, setStudentImportGoogleSheetsUrl] = useState('');
    const [studentImportData, setStudentImportData] = useState<any[]>([]);
    const [studentImportResults, setStudentImportResults] = useState<any>(null);
    const [isImportingStudents, setIsImportingStudents] = useState(false);

    // Class students management state
    const [classStudentsSearchTerm, setClassStudentsSearchTerm] = useState('');
    const [selectedClassStudentIds, setSelectedClassStudentIds] = useState<string[]>([]);
    const [isRemovingMultiple, setIsRemovingMultiple] = useState(false);

    // Import state
    const [showImportModal, setShowImportModal] = useState(false);
    const [importMethod, setImportMethod] = useState<'file' | 'googlesheets'>('file');
    const [file, setFile] = useState<File | null>(null);
    const [googleSheetsUrl, setGoogleSheetsUrl] = useState('');
    const [importData, setImportData] = useState<any[]>([]);
    const [showPreview, setShowPreview] = useState(false);
    const [importResults, setImportResults] = useState<any>(null);
    const [isImporting, setIsImporting] = useState(false);
    const [isDragOver, setIsDragOver] = useState(false);

    // Delete confirmation state
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [classToDelete, setClassToDelete] = useState<any>(null);
    const [isDeleting, setIsDeleting] = useState(false);

    // Student removal confirmation state
    const [showRemoveStudentModal, setShowRemoveStudentModal] = useState(false);
    const [studentToRemove, setStudentToRemove] = useState<any>(null);
    const [isRemovingStudent, setIsRemovingStudent] = useState(false);

    // Exam management state
    const [showExamsModal, setShowExamsModal] = useState(false);
    const [showExamForm, setShowExamForm] = useState(false);
    const [classExams, setClassExams] = useState<any[]>([]);
    const [selectedExam, setSelectedExam] = useState<any>(null);
    const [examsLoading, setExamsLoading] = useState(false);
    const [isExamSubmitting, setIsExamSubmitting] = useState(false);
    const [showDeleteExamModal, setShowDeleteExamModal] = useState(false);
    const [examToDelete, setExamToDelete] = useState<any>(null);
    const [isDeletingExam, setIsDeletingExam] = useState(false);

    // Score management state
    const [showScoresModal, setShowScoresModal] = useState(false);
    const [examScores, setExamScores] = useState<any[]>([]);
    const [scoresLoading, setScoresLoading] = useState(false);
    const [isSavingScores, setIsSavingScores] = useState(false);
    const [selectedExamForScores, setSelectedExamForScores] = useState<any>(null);

    useEffect(() => {
        Promise.all([
            classesApi.getAll(),
            studentsApi.getAll()
        ])
            .then(([classesRes, studentsRes]) => {
                const arr = (classesRes.data.data || []).map(classItem => ({
                    ...classItem,
                    scheduleEntries: Array.isArray(classItem.scheduleEntries) ? classItem.scheduleEntries : []
                }));
                setClasses(arr);
                setAllStudents(studentsRes.data.data || []);
            })
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        // Wait for permissions to load before checking campus access
        if (permissionsLoading) {
            return;
        }

        // Check campus permissions first
        // Check if user has any campus read permission
        const hasCampusAccess = hasAnyPermission([
            { resource: 'campus', action: 'read_all' },
            { resource: 'campus', action: 'read_own' },
            { resource: 'campus', action: 'read' }
        ]);

        // // console.log('🏢 Campus access check:', {
        //     hasCampusAccess,
        //     permissions,
        //     permissionsLoading,
        //     campusReadAll: hasPermission('campus', 'read_all'),
        //     campusReadOwn: hasPermission('campus', 'read_own'),
        //     campusRead: hasPermission('campus', 'read')
        // });

        setCanAccessCampus(hasCampusAccess);

        if (hasCampusAccess) {
            campusApi.getAll()
                .then(res => {
                    const active = (res.data.data || []).filter((c: any) => c.isActive);
                    setCampusOptions(active);
                })
                .catch(err => {
                    console.warn('Failed to load campuses:', err);
                    setCampusOptions([]);
                });
        } else {
            // No campus access - skip loading
            setCampusOptions([]);
        }
    }, [permissionsLoading]); // Only depend on permissionsLoading

    useEffect(() => {
        subjectsApi.getAll()
            .then(res => {
                if (res.data && res.data.data && res.data.data.length) {
                    setSubjectOptions(res.data.data);
                } else {
                    setSubjectOptions([
                        { id: '1', name: 'Toán học' },
                        { id: '2', name: 'Vật lý' },
                        { id: '3', name: 'Hóa học' },
                        { id: '4', name: 'Tiếng Anh' },
                        { id: '5', name: 'Ngữ văn' }
                    ]);
                }
            })
            .catch(() => {
                setSubjectOptions([
                    { id: '1', name: 'Toán học' },
                    { id: '2', name: 'Vật lý' },
                    { id: '3', name: 'Hóa học' },
                    { id: '4', name: 'Tiếng Anh' },
                    { id: '5', name: 'Ngữ văn' }
                ]);
            });
    }, []);

    // Load eligible users (teachers and assistants)
    useEffect(() => {
        const loadEligibleUsers = async () => {
            try {
                const response = await usersApi.getAll();
                // console.log('Full API response:', response); // Debug log

                // Backend returns {data: [users...]} not {data: {success: true, data: [users...]}}
                const allUsers = response.data.data || response.data;
                // console.log('All users from API:', allUsers); // Debug log

                if (Array.isArray(allUsers)) {
                    // Filter users with roles that can be teachers or assistants
                    const eligibleUsersList = allUsers.filter((user: any) =>
                        ['ADMIN', 'MANAGER', 'TEACHER', 'ASSISTANT'].includes(user.role)
                    );
                    // console.log('Eligible users after filter:', eligibleUsersList); // Debug log

                    setEligibleUsers(eligibleUsersList);
                } else {
                    console.error('API response is not an array:', allUsers);
                }
            } catch (error) {
                console.error('Error loading eligible users:', error);
            }
        };

        loadEligibleUsers();
    }, []);    // Form state for comprehensive form handling
    const [formData, setFormData] = useState({
        name: '',
        subject: '',
        campus: '',
        room: '',
        teacherId: '', // Changed from teacher to teacherId
        assistantId: '', // Changed from assistant to assistantId
        startDate: '',
        endDate: '',
        capacity: '',
        fee: '',
        scheduleEntries: [
            { day: '', start: '', end: '' }
        ],
        description: '',
        requirements: '',
        materials: ''
    });

    const [formErrors, setFormErrors] = useState<Record<string, string>>({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Available options for dropdowns
    const [eligibleUsers, setEligibleUsers] = useState<any[]>([]);

    // Exam form data
    const [examFormData, setExamFormData] = useState({
        name: '',
        coefficient: '',
        examDate: '',
        duration: '',
        maxScore: '10',
        examType: 'Kiểm tra',
        description: '',
        status: 'Sắp thi'
    });
    const [examFormErrors, setExamFormErrors] = useState<Record<string, string>>({});

    // Dropdown handling
    const handleDropdownClick = (classId: number, event: React.MouseEvent) => {
        event.stopPropagation();
        setOpenDropdown(openDropdown === classId ? null : classId);
    };

    const handleDropdownAction = (action: string, classItem: any) => {
        // Ensure scheduleEntries is always an array
        const normalizedClassItem = {
            ...classItem,
            scheduleEntries: Array.isArray(classItem.scheduleEntries) ? classItem.scheduleEntries : []
        };
        setSelectedClass(normalizedClassItem);
        setOpenDropdown(null);

        switch (action) {
            case 'view':
                setShowViewModal(true);
                break;
            case 'edit':
                setFormData({
                    name: normalizedClassItem.name || '',
                    subject: (typeof normalizedClassItem.subject === 'object' ? normalizedClassItem.subject?.name : normalizedClassItem.subject) || '',
                    campus: (typeof normalizedClassItem.campus === 'object' ? normalizedClassItem.campus?.id : normalizedClassItem.campus) || normalizedClassItem.campusId || '',
                    room: normalizedClassItem.room || '',
                    teacherId: normalizedClassItem.teacherInfo?.id || '',
                    assistantId: normalizedClassItem.assistantInfo?.id || '',
                    startDate: normalizedClassItem.startDate ? normalizedClassItem.startDate.split('T')[0] : '',
                    endDate: normalizedClassItem.endDate ? normalizedClassItem.endDate.split('T')[0] : '',
                    capacity: (normalizedClassItem.capacity || 0).toString(),
                    fee: (normalizedClassItem.fee || 0).toString(),
                    scheduleEntries: Array.isArray(normalizedClassItem.scheduleEntries) ? normalizedClassItem.scheduleEntries.map(s => ({
                        day: s.day || '',
                        start: s.start || '',
                        end: s.end || ''
                    })) : [],
                    description: normalizedClassItem.description || '',
                    requirements: normalizedClassItem.requirements || '',
                    materials: normalizedClassItem.materials || ''
                });
                setShowEditModal(true);
                break;
            case 'students':
                setShowStudentsModal(true);
                loadClassStudents(normalizedClassItem.id);
                break;
            case 'exams':
                setShowExamsModal(true);
                loadClassExams(normalizedClassItem.id);
                break;
            case 'settings':
                setShowSettingsModal(true);
                break;
            case 'delete':
                setClassToDelete(normalizedClassItem);
                setShowDeleteModal(true);
                break;
        }
    };

    const notify = (message: string, type: 'success' | 'error' | 'info' = 'info') => {
        showUiToast(message, type);
    };

    // Student management functions
    const refreshClasses = async () => {
        try {
            const classesRes = await classesApi.getAll();
            const arr = (classesRes.data.data || []).map(classItem => ({
                ...classItem,
                scheduleEntries: Array.isArray(classItem.scheduleEntries) ? classItem.scheduleEntries : []
            }));
            setClasses(arr);

            // Update selectedClass if it exists
            if (selectedClass) {
                const updatedClass = arr.find(c => c.id === selectedClass.id);
                if (updatedClass) {
                    setSelectedClass(updatedClass);
                }
            }
        } catch (err) {
            console.error('Failed to refresh classes:', err);
        }
    };

    const loadClassStudents = async (classId: string) => {
        setStudentsLoading(true);
        try {
            const studentsRes = await classesApi.getStudents(classId);
            setClassStudents(studentsRes.data.data || []);
            // Only load all students if not already loaded
            if (allStudents.length === 0) {
                const allStudentsRes = await studentsApi.getAll();
                setAllStudents(allStudentsRes.data.data || []);
            }
        } catch (err) {
            console.error('Failed to load students:', err);
            notify('❌ Không thể tải danh sách học sinh!', 'error');
        } finally {
            setStudentsLoading(false);
        }
    };

    const handleEnrollStudent = async (studentId: string) => {
        try {
            await classesApi.enrollStudent(selectedClass.id, studentId);
            notify('✅ Đã thêm học sinh vào lớp!', 'success');
            // Reload both class students and update class list to reflect enrollment count
            await Promise.all([
                loadClassStudents(selectedClass.id),
                refreshClasses()
            ]);
            // Trigger refresh for Students page
            localStorage.setItem('enrollment_updated', Date.now().toString());
            setShowAddStudentModal(false);
        } catch (err: any) {
            notify(err.message || '❌ Không thể thêm học sinh!', 'error');
        }
    };

    const handleEnrollMultipleStudents = async () => {
        if (selectedStudentIds.length === 0) {
            notify('❌ Vui lòng chọn ít nhất một học sinh!', 'error');
            return;
        }

        setIsEnrollingMultiple(true);
        let successCount = 0;
        let errorCount = 0;

        try {
            // Enroll students one by one
            for (const studentId of selectedStudentIds) {
                try {
                    await classesApi.enrollStudent(selectedClass.id, studentId);
                    successCount++;
                } catch (err) {
                    errorCount++;
                    console.error(`Failed to enroll student ${studentId}:`, err);
                }
            }

            // Show results
            if (successCount > 0) {
                notify(`✅ Đã thêm ${successCount} học sinh vào lớp!`, 'success');
            }
            if (errorCount > 0) {
                notify(`❌ Không thể thêm ${errorCount} học sinh!`, 'error');
            }

            // Reload data and close modal
            await Promise.all([
                loadClassStudents(selectedClass.id),
                refreshClasses()
            ]);
            localStorage.setItem('enrollment_updated', Date.now().toString());
            setShowAddStudentModal(false);
            setSelectedStudentIds([]);
        } catch (err: any) {
            notify('❌ Có lỗi xảy ra khi thêm học sinh!', 'error');
        } finally {
            setIsEnrollingMultiple(false);
        }
    };

    const handleSelectStudent = (studentId: string) => {
        setSelectedStudentIds(prev =>
            prev.includes(studentId)
                ? prev.filter(id => id !== studentId)
                : [...prev, studentId]
        );
    };

    const handleSelectAllStudents = () => {
        const availableStudents = allStudents.filter(student =>
            !classStudents.some(cs => cs.id === student.id)
        );

        if (selectedStudentIds.length === availableStudents.length) {
            // Deselect all
            setSelectedStudentIds([]);
        } else {
            // Select all
            setSelectedStudentIds(availableStudents.map(s => s.id));
        }
    };

    // Handle paste event for bulk student search
    const handleStudentSearchPaste = (pastedText: string) => {
        // Support multiple separators: newline, comma, semicolon, tab, pipe
        const separators = ['\n', ',', ';', '\t', '|'];
        let names: string[] = [];

        // Try each separator and use the one that gives the most results
        for (const separator of separators) {
            const splitNames = pastedText.trim().split(separator)
                .map(name => name.trim())
                .filter(name => name !== '');

            if (splitNames.length > names.length) {
                names = splitNames;
            }
        }

        // Also handle mixed separators (e.g., "Name1, Name2\nName3; Name4")
        if (names.length === 1) {
            // If single separator didn't work, try multiple separators
            const mixedSeparatorRegex = /[\n,;\t|]+/;
            names = pastedText.trim().split(mixedSeparatorRegex)
                .map(name => name.trim())
                .filter(name => name !== '');
        }

        if (names.length > 1) {
            // Multiple names pasted - find matching students
            const foundStudents: any[] = [];
            const notFoundNames: string[] = [];
            const alreadyInClassNames: string[] = [];

            names.forEach(name => {
                const searchName = foldText(name);
                const matchingStudent = searchName ? allStudents.find(student => {
                    const studentName = foldText(student.name || '');
                    return Boolean(studentName) && (studentName.includes(searchName) || searchName.includes(studentName));
                }) : undefined;

                if (matchingStudent) {
                    if (classStudents.some(cs => cs.id === matchingStudent.id)) {
                        // Student already in class
                        alreadyInClassNames.push(name.trim());
                    } else {
                        // Student found and not in class
                        foundStudents.push(matchingStudent);
                    }
                } else {
                    // Student not found
                    notFoundNames.push(name.trim());
                }
            });

            // Remove duplicates
            const uniqueFoundStudents = foundStudents.filter((student, index, self) =>
                index === self.findIndex(s => s.id === student.id)
            );

            setFoundStudentsFromPaste(uniqueFoundStudents);

            // Auto-select found students
            const foundStudentIds = uniqueFoundStudents.map(s => s.id);
            setSelectedStudentIds(prev => {
                const newIds = [...new Set([...prev, ...foundStudentIds])];
                return newIds;
            });

            // Show detected separator info
            const detectedSeparator = pastedText.includes('\n') ? 'xuống dòng' :
                pastedText.includes(',') ? 'dấu phẩy' :
                    pastedText.includes(';') ? 'dấu chấm phẩy' :
                        pastedText.includes('\t') ? 'tab' :
                            pastedText.includes('|') ? 'dấu gạch dọc' : 'hỗn hợp';

            // Store separator info and error details for display
            const studentsWithSeparatorInfo = uniqueFoundStudents.map((student, index) => ({
                ...student,
                _separatorInfo: index === 0 ? {
                    separator: detectedSeparator,
                    totalNames: names.length,
                    notFoundNames,
                    alreadyInClassNames
                } : undefined
            }));

            setFoundStudentsFromPaste(studentsWithSeparatorInfo);

            // Clear the search term to show all found students
            setStudentSearchTerm('');

            return true; // Indicate that paste was processed
        }

        return false; // Normal search, not a paste operation
    };

    // Clear found students when search term changes manually
    const handleStudentSearchChange = (value: string) => {
        setStudentSearchTerm(value);
        if (foundStudentsFromPaste.length > 0) {
            setFoundStudentsFromPaste([]);
        }
    };

    // Student import functions
    const resetStudentImport = () => {
        setStudentImportFile(null);
        setStudentImportGoogleSheetsUrl('');
        setStudentImportData([]);
        setStudentImportResults(null);
        setIsImportingStudents(false);
    };

    const handleStudentImportFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        setStudentImportFile(file);
        try {
            const data = await parseStudentImportFile(file);
            setStudentImportData(data);
        } catch (error) {
            notify('❌ Lỗi khi đọc file!', 'error');
        }
    };

    const parseStudentImportFile = async (file: File): Promise<any[]> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => {
                try {
                    const data = new Uint8Array(e.target?.result as ArrayBuffer);
                    const workbook = XLSX.read(data, { type: 'array' });
                    const sheetName = workbook.SheetNames[0];
                    const worksheet = workbook.Sheets[sheetName];
                    const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as any[][];

                    if (jsonData.length < 2) {
                        reject(new Error('File không có dữ liệu hợp lệ'));
                        return;
                    }

                    const headers = jsonData[0];
                    const rows = jsonData.slice(1);

                    const students = rows.map((row, index) => {
                        const student: any = {};
                        headers.forEach((header: string, i: number) => {
                            const normalizedHeader = header.trim().toLowerCase();
                            const value = row[i]?.toString().trim() || '';

                            // Check specific patterns first, then general patterns
                            if (normalizedHeader.includes('tên phụ huynh')) {
                                student.parentName = value;
                            } else if (normalizedHeader.includes('sđt phụ huynh') || normalizedHeader.includes('số điện thoại phụ huynh')) {
                                student.parentPhone = value;
                            } else if (normalizedHeader.includes('họ và tên') || (normalizedHeader.includes('tên') && !normalizedHeader.includes('phụ huynh'))) {
                                student.fullName = value;
                            } else if (normalizedHeader.includes('giới tính')) {
                                student.gender = value;
                            } else if (normalizedHeader.includes('ngày sinh')) {
                                student.dateOfBirth = value;
                            } else if (normalizedHeader.includes('số điện thoại') && !normalizedHeader.includes('phụ huynh')) {
                                student.phone = value;
                            } else if (normalizedHeader.includes('phone') && !normalizedHeader.includes('parent')) {
                                student.phone = value;
                            } else if (normalizedHeader.includes('email')) {
                                student.email = value;
                            } else if (normalizedHeader.includes('địa chỉ')) {
                                student.address = value;
                            }
                        });

                        student.rowIndex = index + 2; // +2 because we skip header and start from 1
                        return student;
                    }).filter(student => student.fullName); // Only include students with names

                    resolve(students);
                } catch (error) {
                    reject(error);
                }
            };
            reader.readAsArrayBuffer(file);
        });
    };

    const handleStudentImportGoogleSheets = async () => {
        if (!studentImportGoogleSheetsUrl.trim()) {
            notify('❌ Vui lòng nhập link Google Sheets!', 'error');
            return;
        }

        try {
            const csvUrl = studentImportGoogleSheetsUrl.replace('/edit#gid=', '/export?format=csv&gid=')
                .replace('/edit?usp=sharing', '/export?format=csv');

            const response = await fetch(csvUrl);
            const csvText = await response.text();

            const lines = csvText.split('\n');
            if (lines.length < 2) {
                notify('❌ File không có dữ liệu hợp lệ!', 'error');
                return;
            }

            const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
            const rows = lines.slice(1).filter(line => line.trim());

            const students = rows.map((row, index) => {
                const values = row.split(',').map(v => v.trim().replace(/"/g, ''));
                const student: any = {};

                headers.forEach((header, i) => {
                    const normalizedHeader = header.toLowerCase();
                    const value = values[i] || '';

                    // Check specific patterns first, then general patterns
                    if (normalizedHeader.includes('tên phụ huynh')) {
                        student.parentName = value;
                    } else if (normalizedHeader.includes('sđt phụ huynh') || normalizedHeader.includes('số điện thoại phụ huynh')) {
                        student.parentPhone = value;
                    } else if (normalizedHeader.includes('họ và tên') || (normalizedHeader.includes('tên') && !normalizedHeader.includes('phụ huynh'))) {
                        student.fullName = value;
                    } else if (normalizedHeader.includes('giới tính')) {
                        student.gender = value;
                    } else if (normalizedHeader.includes('ngày sinh')) {
                        student.dateOfBirth = value;
                    } else if (normalizedHeader.includes('số điện thoại') && !normalizedHeader.includes('phụ huynh')) {
                        student.phone = value;
                    } else if (normalizedHeader.includes('phone') && !normalizedHeader.includes('parent')) {
                        student.phone = value;
                    } else if (normalizedHeader.includes('email')) {
                        student.email = value;
                    } else if (normalizedHeader.includes('địa chỉ')) {
                        student.address = value;
                    }
                });

                student.rowIndex = index + 2;
                return student;
            }).filter(student => student.fullName);

            setStudentImportData(students);
            notify('✅ Đã tải dữ liệu từ Google Sheets!', 'success');
        } catch (error) {
            notify('❌ Lỗi khi tải dữ liệu từ Google Sheets!', 'error');
        }
    };

    const handleImportStudentsToClass = async () => {
        if (!selectedClass || studentImportData.length === 0) return;

        setIsImportingStudents(true);

        try {
            const results = {
                success: [],
                errors: [],
                total: studentImportData.length
            };

            for (const studentData of studentImportData) {
                try {
                    // Find existing student with comprehensive matching
                    const existingStudent = allStudents.find(s => {
                        // Primary match by full name
                        const nameMatch = studentData.fullName && s.name &&
                            s.name.toLowerCase().trim() === studentData.fullName.toLowerCase().trim();

                        if (!nameMatch) {
                            // If name doesn't match, try email or phone as unique identifiers
                            return (
                                (studentData.email && s.email && s.email.toLowerCase().trim() === studentData.email.toLowerCase().trim()) ||
                                (studentData.phone && s.phone && s.phone.trim() === studentData.phone.trim())
                            );
                        }

                        // If name matches, use additional fields to disambiguate
                        const sameNameStudents = allStudents.filter(student =>
                            student.name && student.name.toLowerCase().trim() === studentData.fullName.toLowerCase().trim()
                        );

                        if (sameNameStudents.length === 1) {
                            // Only one student with this name, it's a match
                            return true;
                        }

                        // Multiple students with same name, use additional fields to match
                        let score = 0;
                        let maxScore = 0;

                        // Check email match
                        if (studentData.email && s.email) {
                            maxScore += 10;
                            if (s.email.toLowerCase().trim() === studentData.email.toLowerCase().trim()) {
                                score += 10;
                            }
                        }

                        // Check phone match
                        if (studentData.phone && s.phone) {
                            maxScore += 10;
                            if (s.phone.trim() === studentData.phone.trim()) {
                                score += 10;
                            }
                        }

                        // Check gender match
                        if (studentData.gender && s.gender) {
                            maxScore += 3;
                            if (s.gender.toLowerCase().trim() === studentData.gender.toLowerCase().trim()) {
                                score += 3;
                            }
                        }

                        // Check date of birth match
                        if (studentData.dateOfBirth && s.birthday) {
                            maxScore += 8;
                            const importDate = new Date(studentData.dateOfBirth);
                            const studentDate = new Date(s.birthday);
                            if (importDate.getTime() === studentDate.getTime()) {
                                score += 8;
                            }
                        }

                        // Check parent phone match
                        if (studentData.parentPhone && s.parentPhone) {
                            maxScore += 5;
                            if (s.parentPhone.trim() === studentData.parentPhone.trim()) {
                                score += 5;
                            }
                        }

                        // Check address match
                        if (studentData.address && s.address) {
                            maxScore += 2;
                            if (s.address.toLowerCase().trim().includes(studentData.address.toLowerCase().trim()) ||
                                studentData.address.toLowerCase().trim().includes(s.address.toLowerCase().trim())) {
                                score += 2;
                            }
                        }

                        // Require at least 70% match on available fields, with minimum email or phone match for unique identification
                        const hasUniqueMatch = (studentData.email && s.email &&
                            s.email.toLowerCase().trim() === studentData.email.toLowerCase().trim()) ||
                            (studentData.phone && s.phone && s.phone.trim() === studentData.phone.trim());

                        return hasUniqueMatch || (maxScore > 0 && (score / maxScore) >= 0.7);
                    });

                    if (!existingStudent) {
                        results.errors.push({
                            row: studentData.rowIndex,
                            data: studentData,
                            error: `Học sinh "${studentData.fullName}" chưa tồn tại trong hệ thống`
                        });
                        continue;
                    }

                    // Check if student is already in class
                    const isAlreadyInClass = classStudents.some(cs => cs.id === existingStudent.id);
                    if (isAlreadyInClass) {
                        results.errors.push({
                            row: studentData.rowIndex,
                            data: studentData,
                            error: `Học sinh "${studentData.fullName}" đã có trong lớp học`
                        });
                        continue;
                    }

                    // Add student to class
                    await classesApi.enrollStudent(selectedClass.id, existingStudent.id);

                    results.success.push({
                        row: studentData.rowIndex,
                        data: studentData,
                        student: existingStudent
                    });

                } catch (error: any) {
                    results.errors.push({
                        row: studentData.rowIndex,
                        data: studentData,
                        error: error.response?.data?.message || 'Có lỗi xảy ra'
                    });
                }
            }

            setStudentImportResults(results);

            if (results.success.length > 0) {
                notify(`✅ Đã thêm ${results.success.length} học sinh vào lớp!`, 'success');
                // Reload class students
                await loadClassStudents(selectedClass.id);
            }

            if (results.errors.length > 0) {
                notify(`⚠️ Có ${results.errors.length} học sinh không thể thêm vào lớp!`, 'info');
            }

        } catch (error) {
            notify('❌ Có lỗi xảy ra khi nhập học sinh!', 'error');
        } finally {
            setIsImportingStudents(false);
        }
    };

    // Class students management functions
    const handleSelectClassStudent = (studentId: string) => {
        setSelectedClassStudentIds(prev =>
            prev.includes(studentId)
                ? prev.filter(id => id !== studentId)
                : [...prev, studentId]
        );
    };

    const handleSelectAllClassStudents = () => {
        const filteredStudents = classStudents.filter(student => {
            return matchesStudentSearch(student, classStudentsSearchTerm);
        });

        if (selectedClassStudentIds.length === filteredStudents.length) {
            // Deselect all
            setSelectedClassStudentIds([]);
        } else {
            // Select all visible students
            setSelectedClassStudentIds(filteredStudents.map(s => s.id));
        }
    };

    const handleRemoveMultipleStudents = async () => {
        if (selectedClassStudentIds.length === 0 || !selectedClass) return;

        setIsRemovingMultiple(true);

        try {
            const promises = selectedClassStudentIds.map(studentId =>
                classesApi.removeStudent(selectedClass.id, studentId)
            );

            await Promise.all(promises);

            notify(`✅ Đã xóa ${selectedClassStudentIds.length} học sinh khỏi lớp!`, 'success');

            // Reload class students and clear selection
            await loadClassStudents(selectedClass.id);
            setSelectedClassStudentIds([]);

        } catch (error: any) {
            console.error('Remove multiple students error:', error);
            notify('❌ Có lỗi xảy ra khi xóa học sinh!', 'error');
        } finally {
            setIsRemovingMultiple(false);
        }
    };

    const handleRemoveStudent = (studentId: string) => {
        const student = classStudents.find(s => s.id === studentId);
        if (student) {
            setStudentToRemove(student);
            setShowRemoveStudentModal(true);
        }
    };

    const handleConfirmRemoveStudent = async () => {
        if (!studentToRemove) return;

        setIsRemovingStudent(true);

        try {
            await classesApi.removeStudent(selectedClass.id, studentToRemove.id);
            notify('✅ Đã xóa học sinh khỏi lớp!', 'success');
            // Reload both class students and update class list to reflect enrollment count
            await Promise.all([
                loadClassStudents(selectedClass.id),
                refreshClasses()
            ]);
            // Trigger refresh for Students page
            localStorage.setItem('enrollment_updated', Date.now().toString());
            setShowRemoveStudentModal(false);
            setStudentToRemove(null);
        } catch (err: any) {
            notify(err.message || '❌ Không thể xóa học sinh!', 'error');
        } finally {
            setIsRemovingStudent(false);
        }
    };

    // Exam management functions
    const loadClassExams = async (classId: string) => {
        setExamsLoading(true);
        try {
            // Mock exam data - replace with actual API call
            const mockExams = [
                {
                    id: '1',
                    name: 'Kiểm tra giữa kỳ',
                    coefficient: 2,
                    examDate: '2024-01-15',
                    duration: '90',
                    maxScore: 10,
                    examType: 'Giữa kỳ',
                    description: 'Kiểm tra nội dung từ chương 1 đến chương 3',
                    status: 'Đã hoàn thành'
                },
                {
                    id: '2',
                    name: 'Kiểm tra cuối kỳ',
                    coefficient: 3,
                    examDate: '2024-02-20',
                    duration: '120',
                    maxScore: 10,
                    examType: 'Cuối kỳ',
                    description: 'Kiểm tra toàn bộ nội dung học kỳ',
                    status: 'Sắp thi'
                }
            ];
            setClassExams(mockExams);
        } catch (err) {
            console.error('Failed to load exams:', err);
            notify('❌ Không thể tải danh sách bài thi!', 'error');
        } finally {
            setExamsLoading(false);
        }
    };

    const handleAddExam = () => {
        setExamFormData({
            name: '',
            coefficient: '',
            examDate: '',
            duration: '',
            maxScore: '10',
            examType: 'Kiểm tra',
            description: '',
            status: 'Sắp thi'
        });
        setExamFormErrors({});
        setSelectedExam(null);
        setShowExamForm(true);
    };

    const handleEditExam = (exam: any) => {
        setExamFormData({
            name: exam.name || '',
            coefficient: exam.coefficient?.toString() || '',
            examDate: exam.examDate || '',
            duration: exam.duration?.toString() || '',
            maxScore: exam.maxScore?.toString() || '10',
            examType: exam.examType || 'Kiểm tra',
            description: exam.description || '',
            status: exam.status || 'Sắp thi'
        });
        setExamFormErrors({});
        setSelectedExam(exam);
        setShowExamForm(true);
    };

    const handleDeleteExam = (exam: any) => {
        setExamToDelete(exam);
        setShowDeleteExamModal(true);
    };

    const validateExamForm = (data: typeof examFormData): Record<string, string> => {
        const errors: Record<string, string> = {};

        if (!data.name.trim()) {
            errors.name = 'Tên bài thi là bắt buộc';
        }

        if (!data.coefficient || Number(data.coefficient) <= 0) {
            errors.coefficient = 'Hệ số phải là số dương';
        }

        if (!data.examDate) {
            errors.examDate = 'Ngày thi là bắt buộc';
        }

        if (!data.duration || Number(data.duration) <= 0) {
            errors.duration = 'Thời gian thi phải là số dương';
        }

        if (!data.maxScore || Number(data.maxScore) <= 0) {
            errors.maxScore = 'Điểm tối đa phải là số dương';
        }

        return errors;
    };

    const handleExamSubmit = async () => {
        const errors = validateExamForm(examFormData);
        setExamFormErrors(errors);

        if (Object.keys(errors).length > 0) {
            notify('❌ Vui lòng kiểm tra lại thông tin!', 'error');
            return;
        }

        setIsExamSubmitting(true);

        try {
            // Mock API call - replace with actual API
            const examData = {
                ...examFormData,
                coefficient: Number(examFormData.coefficient),
                duration: Number(examFormData.duration),
                maxScore: Number(examFormData.maxScore),
                classId: selectedClass.id
            };

            if (selectedExam) {
                // Update existing exam
                const updatedExams = classExams.map(exam =>
                    exam.id === selectedExam.id ? { ...exam, ...examData } : exam
                );
                setClassExams(updatedExams);
                notify('✅ Cập nhật bài thi thành công!', 'success');
            } else {
                // Add new exam
                const newExam = {
                    ...examData,
                    id: Date.now().toString()
                };
                setClassExams([...classExams, newExam]);
                notify('✅ Thêm bài thi thành công!', 'success');
            }

            setShowExamForm(false);
            setSelectedExam(null);
        } catch (err: any) {
            notify(err.message || '❌ Không thể lưu bài thi!', 'error');
        } finally {
            setIsExamSubmitting(false);
        }
    };

    const handleConfirmDeleteExam = async () => {
        if (!examToDelete) return;

        setIsDeletingExam(true);

        try {
            // Mock API call - replace with actual API
            setClassExams(classExams.filter(exam => exam.id !== examToDelete.id));
            notify('✅ Đã xóa bài thi thành công!', 'success');
            setShowDeleteExamModal(false);
            setExamToDelete(null);
        } catch (err: any) {
            notify(err.message || '❌ Không thể xóa bài thi!', 'error');
        } finally {
            setIsDeletingExam(false);
        }
    };

    // Score management functions
    const handleManageScores = (exam: any) => {
        setSelectedExamForScores(exam);
        setShowScoresModal(true);
        loadExamScores(exam.id);
    };

    const loadExamScores = async (examId: string) => {
        setScoresLoading(true);
        try {
            // Mock API call - replace with actual API
            // Generate mock scores for all students in the class
            const mockScores = classStudents.map(student => ({
                studentId: student.id,
                studentName: student.name,
                studentEmail: student.email,
                score: Math.random() > 0.3 ? (Math.random() * selectedExamForScores?.maxScore || 10).toFixed(1) : null,
                note: '',
                submittedAt: Math.random() > 0.5 ? new Date().toISOString() : null
            }));

            setExamScores(mockScores);
        } catch (err) {
            console.error('Failed to load exam scores:', err);
            notify('❌ Không thể tải điểm số!', 'error');
        } finally {
            setScoresLoading(false);
        }
    };

    const handleScoreChange = (studentId: string, score: string, note: string = '') => {
        setExamScores(prevScores =>
            prevScores.map(scoreEntry =>
                scoreEntry.studentId === studentId
                    ? { ...scoreEntry, score: score === '' ? null : score, note }
                    : scoreEntry
            )
        );
    };

    const validateScore = (score: string, maxScore: number): boolean => {
        if (score === '' || score === null) return true; // Allow empty scores
        const numScore = parseFloat(score);
        return !isNaN(numScore) && numScore >= 0 && numScore <= maxScore;
    };

    const handleSaveScores = async () => {
        if (!selectedExamForScores) return;

        // Validate all scores
        const invalidScores = examScores.filter(scoreEntry =>
            scoreEntry.score !== null && !validateScore(scoreEntry.score.toString(), selectedExamForScores.maxScore)
        );

        if (invalidScores.length > 0) {
            notify(`❌ Có ${invalidScores.length} điểm không hợp lệ! Điểm phải từ 0 đến ${selectedExamForScores.maxScore}`, 'error');
            return;
        }

        setIsSavingScores(true);

        try {
            // Mock API call - replace with actual API
            await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate API delay

            notify('✅ Đã lưu điểm số thành công!', 'success');

            // Update the exam in classExams to show it has scores
            setClassExams(prevExams =>
                prevExams.map(exam =>
                    exam.id === selectedExamForScores.id
                        ? { ...exam, hasScores: true, scoredStudents: examScores.filter(s => s.score !== null).length }
                        : exam
                )
            );

        } catch (err: any) {
            notify(err.message || '❌ Không thể lưu điểm số!', 'error');
        } finally {
            setIsSavingScores(false);
        }
    };

    const calculateScoreStats = () => {
        const validScores = examScores
            .filter(scoreEntry => scoreEntry.score !== null && scoreEntry.score !== '')
            .map(scoreEntry => parseFloat(scoreEntry.score));

        if (validScores.length === 0) {
            return {
                average: 0,
                highest: 0,
                lowest: 0,
                totalStudents: examScores.length,
                scoredStudents: 0,
                passRate: 0
            };
        }

        const average = validScores.reduce((sum, score) => sum + score, 0) / validScores.length;
        const highest = Math.max(...validScores);
        const lowest = Math.min(...validScores);
        const passThreshold = selectedExamForScores?.maxScore * 0.5 || 5; // 50% to pass
        const passedStudents = validScores.filter(score => score >= passThreshold).length;
        const passRate = (passedStudents / validScores.length) * 100;

        return {
            average: average.toFixed(1),
            highest: highest.toFixed(1),
            lowest: lowest.toFixed(1),
            totalStudents: examScores.length,
            scoredStudents: validScores.length,
            passRate: passRate.toFixed(1)
        };
    };

    // Import functions
    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFile = e.target.files?.[0];
        if (selectedFile) {
            setFile(selectedFile);
            parseFile(selectedFile);
        }
    };

    // Drag and drop handlers
    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(true);
    };

    const handleDragEnter = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(true);
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);

        const files = e.dataTransfer.files;
        if (files && files.length > 0) {
            const droppedFile = files[0];

            // Check file type
            const allowedTypes = ['.csv', '.json', '.xlsx', '.xls'];
            const fileExtension = '.' + droppedFile.name.split('.').pop()?.toLowerCase();

            if (!allowedTypes.includes(fileExtension)) {
                notify('❌ Định dạng file không được hỗ trợ! Chỉ chấp nhận: CSV, JSON, XLSX, XLS', 'error');
                return;
            }

            // Check file size (10MB max)
            const maxSize = 10 * 1024 * 1024; // 10MB in bytes
            if (droppedFile.size > maxSize) {
                notify('❌ File quá lớn! Tối đa 10MB', 'error');
                return;
            }

            setFile(droppedFile);
            parseFile(droppedFile);
        }
    };

    // Process schedule data correctly - convert from Vietnamese weekday format
    const parseSchedule = (scheduleStr: string) => {
        if (!scheduleStr) return {};

        // console.log('parseSchedule input:', scheduleStr);

        const dayMapping: { [key: string]: string } = {
            'Thứ 2': 'monday',
            'Thứ hai': 'monday',
            'Monday': 'monday',
            'Thứ 3': 'tuesday',
            'Thứ ba': 'tuesday',
            'Tuesday': 'tuesday',
            'Thứ 4': 'wednesday',
            'Thứ tư': 'wednesday',
            'Wednesday': 'wednesday',
            'Thứ 5': 'thursday',
            'Thứ năm': 'thursday',
            'Thursday': 'thursday',
            'Thứ 6': 'friday',
            'Thứ sáu': 'friday',
            'Friday': 'friday',
            'Thứ 7': 'saturday',
            'Thứ bảy': 'saturday',
            'Saturday': 'saturday',
            'Chủ nhật': 'sunday',
            'Sunday': 'sunday'
        };

        const schedule: { [key: string]: boolean } = {};

        // Split by commas and process each day
        const days = scheduleStr.split(',').map(day => day.trim());
        // console.log('parseSchedule days:', days);

        days.forEach(day => {
            const englishDay = dayMapping[day];
            // console.log(`Day "${day}" → "${englishDay}"`);
            if (englishDay) {
                schedule[englishDay] = true;
            }
        });

        // console.log('parseSchedule output:', schedule);
        return schedule;
    };

    const parseFile = async (file: File) => {
        try {
            let data: any[] = [];

            if (file.name.endsWith('.csv')) {
                const text = await file.text();
                data = parseCSV(text);
            } else if (file.name.endsWith('.json')) {
                const text = await file.text();
                const jsonData = JSON.parse(text);
                data = Array.isArray(jsonData) ? jsonData : [jsonData];
            } else if (file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
                // Parse Excel files using SheetJS
                const arrayBuffer = await file.arrayBuffer();
                const workbook = XLSX.read(arrayBuffer, { type: 'array' });
                const sheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[sheetName];
                data = XLSX.utils.sheet_to_json(worksheet);
            }

            // Normalize the data (removed teacher, assistant, fee fields)
            const normalizedData = data.map(item => ({
                name: item.name || item['Tên lớp'] || item['Tên'] || '',
                subject: item.subject || item['Môn học'] || '',
                campusName: item.campusName || item['Cơ sở'] || item.campus || '',
                room: item.room || item['Phòng'] || '',
                description: item.description || item['Mô tả'] || '',
                startDate: item.startDate || item['Ngày bắt đầu'] || '',
                endDate: item.endDate || item['Ngày kết thúc'] || '',
                capacity: Number(item.capacity || item['Sức chứa'] || 20),
                startTime: item.startTime || item['Giờ bắt đầu'] || '08:00',
                endTime: item.endTime || item['Giờ kết thúc'] || '10:00',
                schedule: item.schedule || item['Lịch học'] || '', // Send raw string to backend
                scheduleRaw: item.schedule || item['Lịch học'] || '', // Keep raw for display
                scheduleParsed: parseSchedule(item.schedule || item['Lịch học'] || '') // For frontend use
            }));

            setImportData(normalizedData);
            setShowPreview(true);
        } catch (error) {
            notify('❌ Lỗi khi đọc file!', 'error');
            console.error('Parse file error:', error);
        }
    };

    const parseCSV = (text: string): any[] => {
        const lines = text.split('\n').filter(line => line.trim());
        if (lines.length < 2) return [];

        // Parse CSV properly handling quoted values
        const parseCSVLine = (line: string): string[] => {
            const result: string[] = [];
            let current = '';
            let inQuotes = false;

            for (let i = 0; i < line.length; i++) {
                const char = line[i];

                if (char === '"') {
                    inQuotes = !inQuotes;
                } else if (char === ',' && !inQuotes) {
                    result.push(current.trim());
                    current = '';
                } else {
                    current += char;
                }
            }

            result.push(current.trim());
            return result;
        };

        const headers = parseCSVLine(lines[0]);
        const data = [];

        for (let i = 1; i < lines.length; i++) {
            const values = parseCSVLine(lines[i]);
            const item: any = {};

            headers.forEach((header, index) => {
                item[header] = values[index] || '';
            });

            data.push(item);
        }

        return data;
    };

    const handleGoogleSheetsUrl = async () => {
        try {
            let csvUrl = googleSheetsUrl;

            // Convert Google Sheets URL to CSV export URL
            if (googleSheetsUrl.includes('docs.google.com/spreadsheets')) {
                const match = googleSheetsUrl.match(/\/spreadsheets\/d\/([a-zA-Z0-9-_]+)/);
                if (match) {
                    csvUrl = `https://docs.google.com/spreadsheets/d/${match[1]}/export?format=csv`;
                }
            }

            const response = await fetch(csvUrl);
            const text = await response.text();
            const data = parseCSV(text);

            // Debug log to check raw data from Google Sheets
            // console.log('Google Sheets raw data sample:', data.slice(0, 1));

            // Normalize the data (same as parseFile function)
            const normalizedData = data.map(item => ({
                name: item.name || item['Tên lớp'] || item['Tên'] || '',
                subject: item.subject || item['Môn học'] || '',
                campusName: item.campusName || item['Cơ sở'] || item.campus || '',
                room: item.room || item['Phòng'] || '',
                description: item.description || item['Mô tả'] || '',
                startDate: item.startDate || item['Ngày bắt đầu'] || '',
                endDate: item.endDate || item['Ngày kết thúc'] || '',
                capacity: Number(item.capacity || item['Sức chứa'] || 20),
                startTime: item.startTime || item['Giờ bắt đầu'] || '08:00',
                endTime: item.endTime || item['Giờ kết thúc'] || '10:00',
                schedule: item.schedule || item['Lịch học'] || '', // Send raw string to backend
                scheduleRaw: item.schedule || item['Lịch học'] || '', // Keep raw for display
                scheduleParsed: parseSchedule(item.schedule || item['Lịch học'] || '') // For frontend use
            }));

            // console.log('Google Sheets normalized data sample:', normalizedData.slice(0, 1));

            setImportData(normalizedData);
            setShowPreview(true);
        } catch (error) {
            notify('❌ Không thể tải dữ liệu từ Google Sheets!', 'error');
            console.error('Google Sheets error:', error);
        }
    };

    const handleImport = async () => {
        setIsImporting(true);
        try {
            // Debug log to check data being sent
            // console.log('Import data being sent:', importData.slice(0, 1));
            const response = await classesApi.bulkImport(importData);

            setImportResults(response.data.data);
            notify(response.data.message, 'success');
            // Refresh classes list
            const classesRes = await classesApi.getAll();
            const arr = (classesRes.data.data || []).map(classItem => ({
                ...classItem,
                scheduleEntries: Array.isArray(classItem.scheduleEntries) ? classItem.scheduleEntries : []
            }));
            setClasses(arr);
        } catch (error: any) {
            notify(error.message || '❌ Lỗi khi nhập dữ liệu!', 'error');
        } finally {
            setIsImporting(false);
        }
    };

    const resetImport = () => {
        setFile(null);
        setGoogleSheetsUrl('');
        setImportData([]);
        setShowPreview(false);
        setImportResults(null);
        setIsImporting(false);
        setIsDragOver(false);
    };

    const downloadTemplate = () => {
        // Create workbook and worksheet
        const wb = XLSX.utils.book_new();

        // Template data without teacher, assistant, and fee columns
        const templateData = [
            {
                'Tên lớp': 'Toán 10A1',
                'Môn học': 'Toán học',
                'Cơ sở': 'Cơ sở Quận 1',
                'Phòng': 'P101',
                'Mô tả': 'Lớp toán nâng cao',
                'Ngày bắt đầu': '2024-01-15',
                'Ngày kết thúc': '2024-06-15',
                'Sức chứa': 25,
                'Giờ bắt đầu': '08:00',
                'Giờ kết thúc': '10:00',
                'Lịch học': 'Thứ 2, Thứ 4, Thứ 6'
            },
            {
                'Tên lớp': 'Vật lý 11B2',
                'Môn học': 'Vật lý',
                'Cơ sở': 'Cơ sở Quận 7',
                'Phòng': 'P202',
                'Mô tả': 'Lớp vật lý thí nghiệm',
                'Ngày bắt đầu': '2024-01-20',
                'Ngày kết thúc': '2024-06-20',
                'Sức chứa': 20,
                'Giờ bắt đầu': '14:00',
                'Giờ kết thúc': '16:00',
                'Lịch học': 'Thứ 3, Thứ 5'
            }
        ];

        // Create worksheet from data
        const ws = XLSX.utils.json_to_sheet(templateData);

        // Auto-fit column widths
        const colWidths = [
            { wch: 15 }, // Tên lớp
            { wch: 15 }, // Môn học
            { wch: 20 }, // Cơ sở
            { wch: 10 }, // Phòng
            { wch: 25 }, // Mô tả
            { wch: 12 }, // Ngày bắt đầu
            { wch: 12 }, // Ngày kết thúc
            { wch: 10 }, // Sức chứa
            { wch: 12 }, // Giờ bắt đầu
            { wch: 12 }, // Giờ kết thúc
            { wch: 25 }  // Lịch học
        ];
        ws['!cols'] = colWidths;

        // Add worksheet to workbook
        XLSX.utils.book_append_sheet(wb, ws, 'Template Lớp học');

        // Save file
        XLSX.writeFile(wb, 'mau_danh_sach_lop_hoc.xlsx');
    };

    // Form validation
    const validateForm = (data: typeof formData): Record<string, string> => {
        const errors: Record<string, string> = {};

        if (!data.name.trim()) errors.name = 'Tên lớp học là bắt buộc';
        if (!data.subject.trim()) errors.subject = 'Môn học là bắt buộc';
        if (canAccessCampus && (!data.campus || !String(data.campus).trim())) errors.campus = 'Cơ sở là bắt buộc';
        if (!data.room.trim()) errors.room = 'Phòng học là bắt buộc';
        // Note: teacherId is optional in backend, so we don't validate it as required
        // if (!data.teacherId || !String(data.teacherId).trim()) errors.teacherId = 'Giáo viên là bắt buộc';
        if (!data.startDate) errors.startDate = 'Ngày bắt đầu là bắt buộc';
        if (!data.endDate) errors.endDate = 'Ngày kết thúc là bắt buộc';
        if (!data.capacity.trim()) errors.capacity = 'Sức chứa là bắt buộc';
        else if (isNaN(Number(data.capacity)) || Number(data.capacity) <= 0) {
            errors.capacity = 'Sức chứa phải là số dương';
        }
        // Fee is optional, only validate if provided
        if (data.fee.trim() && (isNaN(Number(data.fee)) || Number(data.fee) < 0)) {
            errors.fee = 'Học phí phải là số không âm';
        }
        // validate scheduleEntries
        const validScheduleEntries = data.scheduleEntries.filter(s => s.day && s.start && s.end);
        data.scheduleEntries.forEach((s, idx) => {
            if (s.day || s.start || s.end) { // If any field is filled, all must be filled
                if (!s.day || !s.start || !s.end) {
                    errors[`schedule_${idx}`] = 'Đầy đủ ngày và giờ';
                }
            }
        });
        if (validScheduleEntries.length === 0) errors.schedule = 'Phải thêm ít nhất 1 lịch học';

        if (data.startDate && data.endDate) {
            if (new Date(data.startDate) >= new Date(data.endDate)) {
                errors.endDate = 'Ngày kết thúc phải sau ngày bắt đầu';
            }
        }

        // Debug log for validation
        // console.log('Validation result:', { data, errors });

        return errors;
    };

    // Form submission
    const handleSubmit = async (isEdit: boolean = false) => {
        const errors = validateForm(formData);
        setFormErrors(errors);

        if (Object.keys(errors).length > 0) {
            notify('❌ Vui lòng kiểm tra lại thông tin!', 'error');
            return;
        }

        setIsSubmitting(true);

        try {
            const apiData = {
                name: formData.name,
                subject: formData.subject,
                campusId: formData.campus,
                room: formData.room,
                teacherId: formData.teacherId || undefined, // Send undefined instead of empty string
                assistantId: formData.assistantId || undefined, // Send undefined instead of empty string
                fee: formData.fee.trim() ? Number(formData.fee) : undefined, // Only send fee if provided
                description: formData.description,
                startDate: formData.startDate,
                endDate: formData.endDate,
                capacity: Number(formData.capacity),
                scheduleEntries: formData.scheduleEntries.filter(entry =>
                    entry.day && entry.start && entry.end // Only include complete schedule entries
                )
            };

            // console.log('Sending data to API:', apiData);

            if (isEdit) {
                await classesApi.update(selectedClass.id, apiData);
                notify('✅ Cập nhật lớp học thành công!', 'success');
                // reload list
                const res = await classesApi.getAll();
                const arr = (res.data.data || []).map(classItem => ({
                    ...classItem,
                    scheduleEntries: Array.isArray(classItem.scheduleEntries) ? classItem.scheduleEntries : []
                }));
                setClasses(arr);
                setShowEditModal(false);
            } else {
                const res = await classesApi.create(apiData);
                // The backend now properly returns all fields, add the ones that might be missing for UI display
                const newClass = {
                    ...res.data.data,
                    enrolled: 0,
                    attendanceRate: 0,
                    rating: 0,
                    teacherPhone: 'Chưa có',
                    teacherEmail: 'Chưa có'
                };
                setClasses(prev => [...prev, newClass]);
                notify('✅ Tạo lớp học thành công!', 'success');
                setShowForm(false);
            }
        } catch (err: any) {
            console.error('Error creating/updating class:', err);
            console.error('Form data:', formData);

            // Try to get more specific error message
            let errorMessage = 'Lỗi API';
            if (err.response?.data?.message) {
                errorMessage = err.response.data.message;
            } else if (err.response?.data?.errors) {
                errorMessage = 'Validation errors: ' + JSON.stringify(err.response.data.errors);
            } else if (err.message) {
                errorMessage = err.message;
            }

            notify(`❌ ${errorMessage}`, 'error');
        }

        setFormData({
            name: '', subject: '', campus: '', room: '', teacherId: '', assistantId: '',
            startDate: '', endDate: '', capacity: '', fee: '', scheduleEntries: [{ day: '', start: '', end: '' }],
            description: '', requirements: '', materials: ''
        });
        setFormErrors({});
        setIsSubmitting(false);
    };

    const filteredClasses = classes.filter(classItem => {
        const subjectName = typeof classItem.subject === 'object' ? classItem.subject?.name : classItem.subject;
        const campusId = typeof classItem.campus === 'object' ? classItem.campus?.id : classItem.campus;

        const matchesSearch = matchesQuery([classItem.name, subjectName, classItem.teacher].filter(Boolean).join(' '), searchTerm);
        const matchesCampus = !canAccessCampus || selectedCampus === 'all' || campusId === selectedCampus;
        const matchesStatus = selectedStatus === 'all' || classItem.status === selectedStatus;

        return matchesSearch && matchesCampus && matchesStatus;
    });
    const {
        page: classesPage,
        pageSize: classesPageSize,
        pagedItems: pagedClasses,
        setPage: setClassesPage,
        setPageSize: setClassesPageSize,
    } = useLocalPagination(
        filteredClasses,
        CLASSES_PAGE_SIZE,
        `${searchTerm}|${selectedCampus}|${selectedStatus}|${canAccessCampus}`,
    );

    const getStatusText = (status: string) => {
        switch (status) {
            case 'active': return 'Đang học';
            case 'full': return 'Đầy lớp';
            case 'low_enrollment': return 'Ít học sinh';
            case 'completed': return 'Đã kết thúc';
            default: return status;
        }
    };

    const getStatusTone = (status: string) => {
        switch (status) {
            case 'active': return 'success';
            case 'full': return 'info';
            case 'low_enrollment': return 'warning';
            case 'completed': return 'neutral';
            default: return 'neutral';
        }
    };

    const getDayInVietnamese = (day: string) => {
        const dayMap: Record<string, string> = {
            'monday': 'Thứ 2',
            'tuesday': 'Thứ 3',
            'wednesday': 'Thứ 4',
            'thursday': 'Thứ 5',
            'friday': 'Thứ 6',
            'saturday': 'Thứ 7',
            'sunday': 'Chủ nhật',
            'Thứ 2': 'Thứ 2',
            'Thứ 3': 'Thứ 3',
            'Thứ 4': 'Thứ 4',
            'Thứ 5': 'Thứ 5',
            'Thứ 6': 'Thứ 6',
            'Thứ 7': 'Thứ 7',
            'Chủ nhật': 'Chủ nhật'
        };
        return dayMap[day] || day;
    };

    const handleConfirmDelete = async () => {
        if (!classToDelete) return;

        setIsDeleting(true);
        try {
            await classesApi.delete(classToDelete.id);
            setClasses(prev => prev.filter(c => c.id !== classToDelete.id));
            notify('✅ Đã xóa lớp học thành công!', 'success');

        } catch (error: any) {
            console.error('Error deleting class:', error);
            notify(`❌ Lỗi khi xóa: ${error.message || 'Không thể xóa lớp học'}`, 'error');
        } finally {
            setIsDeleting(false);
            setShowDeleteModal(false);
            setClassToDelete(null);
        }
    };

    const handleCancelDelete = () => {
        setShowDeleteModal(false);
        setClassToDelete(null);
    };

    const resetClassForm = () => {
        setFormData({
            name: '', subject: '', campus: '', room: '', teacherId: '', assistantId: '',
            startDate: '', endDate: '', capacity: '', fee: '', scheduleEntries: [{ day: '', start: '', end: '' }],
            description: '', requirements: '', materials: ''
        });
        setFormErrors({});
    };

    const closeEditClassModal = () => {
        setShowEditModal(false);
        resetClassForm();
    };

    const closeAddStudentModal = () => {
        setShowAddStudentModal(false);
        setSelectedStudentIds([]);
        setFoundStudentsFromPaste([]);
        setStudentSearchTerm('');
        setShowPasteInstructions(false);
    };

    const availableStudentsForEnrollment = allStudents.filter(student => !classStudents.some(cs => cs.id === student.id));

    const dayOptions = [
        { value: 'monday', label: 'Thứ 2' },
        { value: 'tuesday', label: 'Thứ 3' },
        { value: 'wednesday', label: 'Thứ 4' },
        { value: 'thursday', label: 'Thứ 5' },
        { value: 'friday', label: 'Thứ 6' },
        { value: 'saturday', label: 'Thứ 7' },
        { value: 'sunday', label: 'Chủ nhật' }
    ];

    const renderClassFormFields = () => (
        <div className="space-y-5">
            <div className="grid gap-4 md:grid-cols-2">
                <Input
                    label="Tên lớp học *"
                    value={formData.name}
                    onChange={(e) => {
                        setFormData({ ...formData, name: e.target.value });
                        if (formErrors.name) setFormErrors({ ...formErrors, name: '' });
                    }}
                    placeholder="VD: Toán 10A1"
                    error={formErrors.name}
                />
                <SearchSelect
                    label="Môn học *"
                    value={formData.subject}
                    onChange={(value) => {
                        setFormData({ ...formData, subject: value });
                        if (formErrors.subject) setFormErrors({ ...formErrors, subject: '' });
                    }}
                    options={subjectOptions.map((s: any) => ({ value: s.name, label: s.name }))}
                    creatable={{ label: (text) => `Thêm môn mới "${text}"` }}
                    placeholder="Chọn hoặc nhập môn học"
                    error={formErrors.subject}
                />
            </div>

            <div className="grid gap-4 md:grid-cols-2">
                {canAccessCampus ? (
                    <Select
                        label="Cơ sở *"
                        value={formData.campus}
                        onChange={(e) => {
                            setFormData({ ...formData, campus: e.target.value });
                            if (formErrors.campus) setFormErrors({ ...formErrors, campus: '' });
                        }}
                        error={formErrors.campus}
                    >
                        <option value="">Chọn cơ sở</option>
                        {campusOptions.map(c => (
                            <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                    </Select>
                ) : (
                    <div className="space-y-1.5">
                        <label className="block text-sm font-medium text-ink-700">Cơ sở</label>
                        <div className="min-h-10 rounded-lg border border-ink-200 bg-ink-50 px-3 py-2 text-sm text-ink-500">
                            Không có quyền xem danh sách cơ sở
                        </div>
                    </div>
                )}
                <Input
                    label="Phòng học *"
                    value={formData.room}
                    onChange={(e) => {
                        setFormData({ ...formData, room: e.target.value });
                        if (formErrors.room) setFormErrors({ ...formErrors, room: '' });
                    }}
                    placeholder="VD: P101"
                    error={formErrors.room}
                />
            </div>

            <div className="grid gap-4 md:grid-cols-2">
                <SearchSelect
                    label="Giáo viên"
                    value={formData.teacherId}
                    onChange={(value) => {
                        setFormData({ ...formData, teacherId: value });
                        if (formErrors.teacherId) setFormErrors({ ...formErrors, teacherId: '' });
                    }}
                    options={personPickerOptions(eligibleUsers)}
                    placeholder="Chọn giáo viên"
                    clearable
                    error={formErrors.teacherId}
                />
                <SearchSelect
                    label="Trợ giảng"
                    value={formData.assistantId}
                    onChange={(value) => setFormData({ ...formData, assistantId: value })}
                    options={personPickerOptions(eligibleUsers)}
                    placeholder="Chọn trợ giảng (tùy chọn)"
                    clearable
                />
            </div>

            <div className="grid gap-4 md:grid-cols-2">
                <DateInput
                    label="Ngày bắt đầu *"
                    value={formData.startDate}
                    onChange={(value) => {
                        setFormData({ ...formData, startDate: value });
                        if (formErrors.startDate) setFormErrors({ ...formErrors, startDate: '' });
                    }}
                    error={formErrors.startDate}
                />
                <DateInput
                    label="Ngày kết thúc *"
                    value={formData.endDate}
                    onChange={(value) => {
                        setFormData({ ...formData, endDate: value });
                        if (formErrors.endDate) setFormErrors({ ...formErrors, endDate: '' });
                    }}
                    error={formErrors.endDate}
                />
            </div>

            <Input
                label="Sức chứa *"
                type="number"
                value={formData.capacity}
                onChange={(e) => {
                    setFormData({ ...formData, capacity: e.target.value });
                    if (formErrors.capacity) setFormErrors({ ...formErrors, capacity: '' });
                }}
                placeholder="25"
                error={formErrors.capacity}
            />

            <div className="space-y-2">
                <label className="block text-sm font-medium text-ink-700">Lịch học *</label>
                <div className="space-y-2">
                    {formData.scheduleEntries.map((entry, idx) => (
                        <div key={idx} className="grid gap-2 rounded-lg border border-ink-200 bg-ink-50 p-3 sm:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)_minmax(0,1fr)_auto] sm:items-end">
                            <Select
                                aria-label={`Ngày học ${idx + 1}`}
                                value={entry.day}
                                onChange={(e) => {
                                    const copy = [...formData.scheduleEntries];
                                    copy[idx].day = e.target.value;
                                    setFormData({ ...formData, scheduleEntries: copy });
                                }}
                            >
                                <option value="">Ngày</option>
                                {dayOptions.map(d => (
                                    <option key={d.value} value={d.value}>{d.label}</option>
                                ))}
                            </Select>
                            <Input
                                aria-label={`Giờ bắt đầu ${idx + 1}`}
                                type="time"
                                value={entry.start}
                                onChange={(e) => {
                                    const copy = [...formData.scheduleEntries];
                                    copy[idx].start = e.target.value;
                                    setFormData({ ...formData, scheduleEntries: copy });
                                }}
                            />
                            <Input
                                aria-label={`Giờ kết thúc ${idx + 1}`}
                                type="time"
                                value={entry.end}
                                onChange={(e) => {
                                    const copy = [...formData.scheduleEntries];
                                    copy[idx].end = e.target.value;
                                    setFormData({ ...formData, scheduleEntries: copy });
                                }}
                            />
                            <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                icon={<X className="h-4 w-4" />}
                                aria-label={`Xóa lịch học ${idx + 1}`}
                                onClick={() => {
                                    const copy = formData.scheduleEntries.filter((_, i) => i !== idx);
                                    setFormData({ ...formData, scheduleEntries: copy });
                                }}
                            />
                            {formErrors[`schedule_${idx}`] ? (
                                <p className="text-sm text-danger-600 sm:col-span-4">{formErrors[`schedule_${idx}`]}</p>
                            ) : null}
                        </div>
                    ))}
                </div>
                <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    icon={<Plus className="h-4 w-4" />}
                    onClick={() => setFormData({ ...formData, scheduleEntries: [...formData.scheduleEntries, { day: '', start: '', end: '' }] })}
                >
                    Thêm lịch
                </Button>
                {formErrors.schedule && <p className="text-sm text-danger-600">{formErrors.schedule}</p>}
            </div>

            <Textarea
                label="Mô tả"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Mô tả về lớp học..."
                rows={3}
            />
        </div>
    );

    const stats = [
        {
            label: 'Tổng lớp học',
            value: classes.length.toString(),
            change: '+2',
            icon: GraduationCap,
            color: 'bg-academy-500'
        },
        {
            label: 'Đang hoạt động',
            value: classes.filter(c => c.status === 'active').length.toString(),
            change: '+1',
            icon: BookOpen,
            color: 'bg-success-500'
        },
        {
            label: 'Tổng học sinh',
            value: classes.reduce((sum, c) => sum + (c.enrolled || 0), 0).toString(),
            change: '+12',
            icon: Users,
            color: 'bg-academy-500'
        },
        // {
        //   label: 'Doanh thu/Tháng',
        //   value: '₫' + (classes.reduce((sum, c) => sum + (c.fee || 0) * (c.enrolled || 0), 0) / 1000000).toFixed(1) + 'M',
        //   change: '+8%',
        //   icon: DollarSign,
        //   color: 'bg-warning-500'
        // }
    ];

    return (
        <div className="space-y-6">
            <PageHeader
                title="Lớp học"
                subtitle={`${classes.length} lớp · lịch học, giáo viên và sĩ số từng lớp.`}
                actions={hasPermission('class', 'create') ? (
                    <>
                            <Button
                                variant="secondary"
                                icon={<Upload className="h-4 w-4" />}
                                onClick={() => {
                                    resetImport();
                                    setShowImportModal(true);
                                }}
                            >
                                Nhập danh sách lớp học
                            </Button>
                            <Button
                                icon={<Plus className="h-4 w-4" />}
                                onClick={() => {
                                    // Reset form data when opening create form
                                    setFormData({
                                        name: '', subject: '', campus: '', room: '', teacherId: '', assistantId: '',
                                        startDate: '', endDate: '', capacity: '', fee: '', scheduleEntries: [{ day: '', start: '', end: '' }],
                                        description: '', requirements: '', materials: ''
                                    });
                                    setFormErrors({});
                                    setShowForm(true);
                                }}
                            >
                                Thêm lớp học
                            </Button>
                    </>
                ) : null}
            />

            {/* Stats Cards */}
            <div className={`${statGroupClassName} grid-cols-3`}>
                {stats.map((stat) => (
                    <StatCard key={stat.label} label={stat.label} value={stat.value} />
                ))}
            </div>

            <DataToolbar
                className="sm:items-center"
                controlsLayoutClassName="grid flex-1 gap-2 sm:grid-cols-2 lg:flex lg:flex-wrap lg:items-center"
            >
                <Input
                    type="text"
                    placeholder="Tìm lớp, môn, giáo viên…"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    leftIcon={<Search className="h-4 w-4" />}
                    aria-label="Tìm kiếm lớp học"
                    className="min-h-9 lg:w-72"
                />
                {canAccessCampus && (
                    <Select
                        className="min-h-9 lg:w-44"
                        value={selectedCampus}
                        onChange={(e) => setSelectedCampus(e.target.value)}
                        aria-label="Lọc theo cơ sở"
                    >
                        <option value="all">Tất cả cơ sở</option>
                        {campusOptions.map(c => (
                            <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                    </Select>
                )}
                <Select
                    className="min-h-9 lg:w-44"
                    value={selectedStatus}
                    onChange={(e) => setSelectedStatus(e.target.value)}
                    aria-label="Lọc theo trạng thái"
                >
                    <option value="all">Mọi trạng thái</option>
                    <option value="active">Đang học</option>
                    <option value="full">Đầy lớp</option>
                    <option value="low_enrollment">Ít học sinh</option>
                </Select>
            </DataToolbar>

            {/* Classes Cards/Table */}
            {filteredClasses.length === 0 ? (
                <div className="rounded-lg border border-ink-200 bg-white p-8 text-center">
                    <h3 className="mb-1 text-sm font-medium text-ink-900">Không tìm thấy lớp học</h3>
                    <p className="mb-4 text-sm text-ink-500">Thử thay đổi bộ lọc hoặc tạo lớp học mới.</p>
                    {hasPermission('class', 'create') && (
                        <button
                            onClick={() => {
                                // Reset form data when opening create form from empty state
                                setFormData({
                                    name: '', subject: '', campus: '', room: '', teacherId: '', assistantId: '',
                                    startDate: '', endDate: '', capacity: '', fee: '', scheduleEntries: [{ day: '', start: '', end: '' }],
                                    description: '', requirements: '', materials: ''
                                });
                                setFormErrors({});
                                setShowForm(true);
                            }}
                            className="bg-academy-600 hover:bg-academy-700 text-white px-4 py-2 rounded-lg transition-colors duration-200 text-sm font-medium"
                        >
                            Thêm lớp học mới
                        </button>
                    )}
                </div>
            ) : (
                <>
                <ResponsiveDataView
                    desktopLabel="Bảng danh sách lớp học"
                    mobileLabel="Thẻ danh sách lớp học"
                    mobile={
                        <div className="grid gap-3">
                            {pagedClasses.map((classItem) => (
                                <article
                                    key={classItem.id}
                                    onClick={openOnRowClick(() => handleDropdownAction('view', classItem))}
                                    className="cursor-pointer rounded-lg border border-ink-200 bg-white p-4 transition-colors hover:bg-ink-50"
                                >
                                    {/* Header */}
                                    <div>
                                        <div className="mb-4 grid grid-cols-[minmax(0,1fr)_auto] items-start gap-3">
                                            <div className="flex min-w-0 items-start gap-3">
                                                <div className="min-w-0">
                                                    <h3 className="truncate text-base font-semibold text-ink-900">
                                                        <button
                                                            type="button"
                                                            onClick={() => handleDropdownAction('view', classItem)}
                                                            aria-label={`Xem chi tiết lớp ${classItem.name || 'chưa có tên'}`}
                                                            className="max-w-full cursor-pointer truncate text-left"
                                                        >
                                                            {classItem.name}
                                                        </button>
                                                    </h3>
                                                    <p className="truncate text-sm text-ink-500">{(typeof classItem.subject === 'object' ? classItem.subject?.name : classItem.subject) || '?'}</p>
                                                    <div className="mt-2">
                                                        <StatusBadge tone={getStatusTone(classItem.status)}>
                                                            {getStatusText(classItem.status)}
                                                        </StatusBadge>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="flex justify-end">
                                                <div className="relative" onClick={(e) => e.stopPropagation()}>
                                                    <button
                                                        onClick={(e) => handleDropdownClick(classItem.id, e)}
                                                        className={`rounded-lg p-2 text-ink-500 transition-all duration-200 ${openDropdown === classItem.id ? 'bg-academy-50 opacity-100' : 'hover:bg-ink-100'
                                                            }`}
                                                        aria-label="Mở thao tác lớp học"
                                                    >
                                                        <MoreHorizontal className="h-4 w-4" />
                                                    </button>

                                                    {openDropdown === classItem.id && (
                                                        <div className="absolute right-0 mt-1 w-48 bg-white rounded-lg shadow-raised border border-ink-200 z-50">
                                                            <div className="py-1">
                                                                <button
                                                                    onClick={() => handleDropdownAction('view', classItem)}
                                                                    className="flex items-center w-full px-4 py-2 text-sm text-ink-700 hover:bg-ink-100 transition-colors duration-200"
                                                                >
                                                                    <Eye className="w-4 h-4 mr-3" />
                                                                    Xem chi tiết
                                                                </button>
                                                                <button
                                                                    onClick={() => handleDropdownAction('edit', classItem)}
                                                                    className="flex items-center w-full px-4 py-2 text-sm text-ink-700 hover:bg-ink-100 transition-colors duration-200"
                                                                >
                                                                    <Edit2 className="w-4 h-4 mr-3" />
                                                                    Chỉnh sửa
                                                                </button>
                                                                <button
                                                                    onClick={() => handleDropdownAction('students', classItem)}
                                                                    className="flex items-center w-full px-4 py-2 text-sm text-ink-700 hover:bg-ink-100 transition-colors duration-200"
                                                                >
                                                                    <Users className="w-4 h-4 mr-3" />
                                                                    Quản lý học sinh
                                                                </button>
                                                                <button
                                                                    onClick={() => handleDropdownAction('exams', classItem)}
                                                                    className="flex items-center w-full px-4 py-2 text-sm text-ink-700 hover:bg-ink-100 transition-colors duration-200"
                                                                >
                                                                    <FileText className="w-4 h-4 mr-3" />
                                                                    Quản lý bài thi
                                                                </button>
                                                                <button
                                                                    onClick={() => handleDropdownAction('settings', classItem)}
                                                                    className="flex items-center w-full px-4 py-2 text-sm text-ink-700 hover:bg-ink-100 transition-colors duration-200"
                                                                >
                                                                    <Settings className="w-4 h-4 mr-3" />
                                                                    Cài đặt
                                                                </button>
                                                                <div className="border-t border-ink-200 my-1"></div>
                                                                <button
                                                                    onClick={() => handleDropdownAction('delete', classItem)}
                                                                    className="flex items-center w-full px-4 py-2 text-sm text-danger-600 hover:bg-danger-50 transition-colors duration-200"
                                                                >
                                                                    <Trash2 className="w-4 h-4 mr-3" />
                                                                    Xóa lớp
                                                                </button>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Class Info */}
                                        <div className="space-y-3">
                                            <div className="flex items-center text-sm text-ink-600">
                                                <MapPin className="mr-2 h-4 w-4 shrink-0" />
                                                <span>{getCampusDisplayName(classItem.campus)} - {classItem.room || '?'}</span>
                                            </div>

                                            <div className="flex items-center text-sm text-ink-600">
                                                <Clock className="mr-2 h-4 w-4 shrink-0" />
                                                <span>{
                                                    Array.isArray(classItem.scheduleEntries) && classItem.scheduleEntries.length > 0
                                                        ? classItem.scheduleEntries.map(s => `${getDayInVietnamese(s.day || '')} ${s.start || ''}-${s.end || ''}`).join(', ')
                                                        : 'Chưa xác định'
                                                }</span>
                                            </div>

                                            <div className="flex items-center text-sm text-ink-600">
                                                <Calendar className="mr-2 h-4 w-4 shrink-0" />
                                                <span>{classItem.startDate ? new Date(classItem.startDate).toLocaleDateString('vi-VN') : '?'} - {classItem.endDate ? new Date(classItem.endDate).toLocaleDateString('vi-VN') : '?'}</span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Stats */}
                                    <p className="mt-3 text-sm text-ink-600">
                                        Học sinh: <span className="font-semibold tabular-nums text-ink-900">{classItem.enrolled || 0}/{classItem.capacity || 0}</span>
                                    </p>

                                    {/* Teachers */}
                                    <div className="mt-4 border-t border-ink-200 pt-4">
                                        <div className="space-y-2">
                                            <div className="flex items-center justify-between">
                                                <span className="text-sm text-ink-500">Giáo viên:</span>
                                                <span className="text-sm font-medium text-ink-900">
                                                    {classItem.teacherInfo?.name || classItem.teacher || 'Chưa có'}
                                                </span>
                                            </div>
                                            <div className="flex items-center justify-between">
                                                <span className="text-sm text-ink-500">Trợ giảng:</span>
                                                <span className="text-sm font-medium text-ink-900">
                                                    {classItem.assistantInfo?.name || classItem.assistant || 'Chưa có'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                </article>
                            ))}
                        </div>
                    }
                    desktop={
                        <div className="overflow-hidden rounded-lg border border-ink-200 bg-white">
                            <div className="min-w-[980px]">
                                <table className="min-w-full divide-y divide-ink-200 text-sm">
                                    <thead>
                                        <tr>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-ink-500">
                                                Lớp học
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-ink-500">
                                                Địa điểm & Lịch học
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-ink-500">
                                                Giáo viên
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-ink-500">
                                                Trạng thái
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-ink-500">
                                                Học sinh
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-ink-500">
                                                Thời gian
                                            </th>
                                            <th className="w-12 px-3 py-3"><span className="sr-only">Tuỳ chỉnh</span></th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-ink-200">
                                        {pagedClasses.map((classItem) => (
                                            <tr key={classItem.id} onClick={openOnRowClick(() => handleDropdownAction('view', classItem))} className="cursor-pointer hover:bg-ink-50">
                                                <td className="max-w-0 break-words px-4 py-4">
                                                    <div className="min-w-0">
                                                        <div className="min-w-0">
                                                            <button
                                                                type="button"
                                                                onClick={() => handleDropdownAction('view', classItem)}
                                                                aria-label={`Xem chi tiết lớp ${classItem.name || 'chưa có tên'}`}
                                                                className="cursor-pointer whitespace-nowrap text-left text-sm font-medium text-ink-900 hover:text-academy-600"
                                                            >
                                                                {classItem.name}
                                                            </button>
                                                            <div className="break-words text-sm text-ink-500">
                                                                {(typeof classItem.subject === 'object' ? classItem.subject?.name : classItem.subject) || '?'}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="max-w-0 break-words px-4 py-4">
                                                    <div className="flex items-start text-sm text-ink-800">
                                                        <MapPin className="w-3 h-3 mr-1 text-ink-400" />
                                                        <span className="min-w-0 break-words">{getCampusDisplayName(classItem.campus)} - {classItem.room || '?'}</span>
                                                    </div>
                                                    <div className="mt-1 flex items-start text-sm text-ink-500">
                                                        <Clock className="w-3 h-3 mr-1 text-ink-400" />
                                                        <span className="min-w-0 break-words">{Array.isArray(classItem.scheduleEntries) && classItem.scheduleEntries.length > 0
                                                            ? classItem.scheduleEntries.map(s => `${getDayInVietnamese(s.day || '')} ${s.start || ''}-${s.end || ''}`).join(', ')
                                                            : 'Chưa xác định'}</span>
                                                    </div>
                                                </td>
                                                <td className="max-w-0 break-words px-4 py-4">
                                                    <div className="break-words text-sm font-medium text-ink-900">
                                                        {classItem.teacherInfo?.name || classItem.teacher || 'Chưa có'}
                                                    </div>
                                                    <div className="break-words text-sm text-ink-500">
                                                        {classItem.assistantInfo?.name || classItem.assistant || 'Chưa có trợ giảng'}
                                                    </div>
                                                </td>
                                                <td className="px-4 py-4">
                                                    <StatusBadge tone={getStatusTone(classItem.status)}>
                                                        {getStatusText(classItem.status)}
                                                    </StatusBadge>
                                                </td>
                                                <td className="px-4 py-4 text-sm text-ink-800">
                                                    <div className="flex items-center">
                                                        <Users className="w-3 h-3 mr-1 text-action-600" />
                                                        <span>{classItem.enrolled || 0}/{classItem.capacity || 0}</span>
                                                    </div>
                                                </td>
                                                <td className="max-w-0 break-words px-4 py-4 text-sm text-ink-800">
                                                    <div className="space-y-1">
                                                        <div>{classItem.startDate ? new Date(classItem.startDate).toLocaleDateString('vi-VN') : '?'}</div>
                                                        <div className="text-ink-500">đến {classItem.endDate ? new Date(classItem.endDate).toLocaleDateString('vi-VN') : '?'}</div>
                                                    </div>
                                                </td>
                                                <td className="px-2 py-4 text-center">
                                                    <ActionMenu
                                                        open={openDropdown === classItem.id}
                                                        onOpenChange={(open) => setOpenDropdown(open ? classItem.id : null)}
                                                        triggerLabel={`Tuỳ chỉnh lớp ${classItem.name || 'chưa có tên'}`}
                                                        triggerTitle="Tuỳ chỉnh"
                                                        triggerIcon={<MoreHorizontal className="h-4 w-4" />}
                                                        buttonSize="icon"
                                                        width={208}
                                                    >
                                                                <button
                                                                    type="button"
                                                                    role="menuitem"
                                                                    onClick={() => handleDropdownAction('edit', classItem)}
                                                                    className="flex w-full items-center px-4 py-2 text-sm text-ink-700 transition-colors hover:bg-ink-100"
                                                                >
                                                                    <Edit2 className="mr-3 h-4 w-4" />
                                                                    Chỉnh sửa
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    role="menuitem"
                                                                    onClick={() => handleDropdownAction('students', classItem)}
                                                                    className="flex w-full items-center px-4 py-2 text-sm text-ink-700 transition-colors hover:bg-ink-100"
                                                                >
                                                                    <Users className="mr-3 h-4 w-4" />
                                                                    Quản lý học sinh
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    role="menuitem"
                                                                    onClick={() => handleDropdownAction('exams', classItem)}
                                                                    className="flex w-full items-center px-4 py-2 text-sm text-ink-700 transition-colors hover:bg-ink-100"
                                                                >
                                                                    <FileText className="mr-3 h-4 w-4" />
                                                                    Quản lý bài thi
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    role="menuitem"
                                                                    onClick={() => handleDropdownAction('settings', classItem)}
                                                                    className="flex w-full items-center px-4 py-2 text-sm text-ink-700 transition-colors hover:bg-ink-100"
                                                                >
                                                                    <Settings className="mr-3 h-4 w-4" />
                                                                    Cài đặt
                                                                </button>
                                                                <div className="my-1 border-t border-ink-200" />
                                                                <button
                                                                    type="button"
                                                                    role="menuitem"
                                                                    onClick={() => handleDropdownAction('delete', classItem)}
                                                                    className="flex w-full items-center px-4 py-2 text-sm text-danger-600 transition-colors hover:bg-danger-50"
                                                                >
                                                                    <Trash2 className="mr-3 h-4 w-4" />
                                                                    Xóa lớp
                                                                </button>
                                                    </ActionMenu>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    }
                />
                <LocalPagination
                    label="danh sách lớp học"
                    page={classesPage}
                    pageSize={classesPageSize}
                    totalItems={filteredClasses.length}
                    pageSizeOptions={[10, 20, 50]}
                    onPageChange={setClassesPage}
                    onPageSizeChange={setClassesPageSize}
                />
                </>
            )}

            {/* Form Modal */}
            {showForm && (
                <Modal
                    open={showForm}
                    title="Thêm lớp học mới"
                    onClose={() => setShowForm(false)}
                    className="sm:max-w-2xl"
                    footer={
                        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                            <Button variant="secondary" onClick={() => setShowForm(false)} disabled={isSubmitting}>
                                Hủy
                            </Button>
                            <Button onClick={() => handleSubmit(false)} loading={isSubmitting}>
                                {isSubmitting ? 'Đang tạo...' : 'Thêm lớp học'}
                            </Button>
                        </div>
                    }
                >
                    {renderClassFormFields()}
                </Modal>
            )}

            {/* View Details Modal */}
            {showViewModal && selectedClass && (
                <Modal
                    open={showViewModal}
                    title="Chi tiết lớp học"
                    onClose={() => setShowViewModal(false)}
                    className="sm:max-w-3xl lg:max-w-6xl"
                    footer={(
                        <DetailFooter
                            onClose={() => setShowViewModal(false)}
                            onEdit={hasPermission('class', 'update') ? () => {
                                setShowViewModal(false);
                                handleDropdownAction('edit', selectedClass);
                            } : undefined}
                        />
                    )}
                >
                    <div className="space-y-4">
                        <DetailHeader
                            icon={<GraduationCap className="h-6 w-6" />}
                            title={selectedClass.name}
                            subtitle={(typeof selectedClass.subject === 'object' ? selectedClass.subject?.name : selectedClass.subject) || 'Chưa có môn học'}
                            badge={(
                                <StatusBadge tone={getStatusTone(selectedClass.status)}>
                                    {getStatusText(selectedClass.status)}
                                </StatusBadge>
                            )}
                        />
                        <dl className="grid grid-cols-2 gap-x-6 gap-y-4 rounded-lg border border-ink-200 p-4 text-sm lg:grid-cols-4">
                            <ClassFact label="Sĩ số" value={`${selectedClass.enrolled || 0}/${selectedClass.capacity || 0} học sinh`} />
                            <ClassFact label="Cơ sở · Phòng" value={[getCampusDisplayName(selectedClass.campus), selectedClass.room].filter(Boolean).join(' · ')} />
                            <ClassFact
                                label="Lịch học"
                                value={selectedClass.scheduleEntries?.length ? (
                                    <span className="block space-y-0.5">
                                        {selectedClass.scheduleEntries.map((entry, index) => (
                                            <span key={index} className="block">
                                                {getDayInVietnamese(entry.day || '')} {entry.start || ''}–{entry.end || ''}
                                            </span>
                                        ))}
                                    </span>
                                ) : null}
                            />
                            <ClassFact
                                label="Thời gian khoá"
                                value={selectedClass.startDate
                                    ? `${new Date(selectedClass.startDate).toLocaleDateString('vi-VN')} – ${selectedClass.endDate ? new Date(selectedClass.endDate).toLocaleDateString('vi-VN') : 'chưa định'}`
                                    : null}
                            />
                            <ClassFact
                                label="Giáo viên"
                                value={staffDisplayName(selectedClass.teacherInfo, selectedClass.teacher)}
                                hint={[selectedClass.teacherInfo?.phone, selectedClass.teacherInfo?.email].filter(Boolean).join(' · ')}
                            />
                            <ClassFact label="Trợ giảng" value={staffDisplayName(selectedClass.assistantInfo, selectedClass.assistant)} />
                            {selectedClass.description ? <ClassFact label="Mô tả" value={selectedClass.description} wide /> : null}
                            {selectedClass.requirements ? <ClassFact label="Yêu cầu" value={selectedClass.requirements} wide /> : null}
                            {selectedClass.materials ? <ClassFact label="Tài liệu cần thiết" value={selectedClass.materials} wide /> : null}
                        </dl>
                        {/* Pay is admin-only, so is the class's shift type. */}
                        {user?.role === 'ADMIN' ? (
                            <div className="space-y-3 border-t border-ink-200 pt-5">
                                <h3 className="text-base font-semibold text-ink-900">Loại ca (tính lương)</h3>
                                <ClassShiftType classId={selectedClass.id} />
                            </div>
                        ) : null}
                        <div className="space-y-3 border-t border-ink-200 pt-5">
                            <h3 className="text-base font-semibold text-ink-900">Sổ điểm</h3>
                            <ClassGradebook classId={selectedClass.id} />
                        </div>
                    </div>
                </Modal>
            )}

            {/* Edit Modal */}
            {showEditModal && selectedClass && (
                <Modal
                    open={showEditModal}
                    title="Chỉnh sửa lớp học"
                    onClose={closeEditClassModal}
                    className="sm:max-w-2xl"
                    footer={
                        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                            <Button variant="secondary" onClick={closeEditClassModal} disabled={isSubmitting}>
                                Hủy
                            </Button>
                            <Button onClick={() => handleSubmit(true)} loading={isSubmitting}>
                                {isSubmitting ? 'Đang cập nhật...' : 'Cập nhật'}
                            </Button>
                        </div>
                    }
                >
                    {renderClassFormFields()}
                </Modal>
            )}

            {/* Students Management Modal */}
            {showStudentsModal && selectedClass && (
                <Modal
                    open={showStudentsModal}
                    title={`Quản lý học sinh - ${selectedClass.name}`}
                    onClose={() => {
                            setShowStudentsModal(false);
                            setClassStudents([]);
                            setAllStudents([]);
                            setClassStudentsSearchTerm('');
                            setSelectedClassStudentIds([]);
                        }}
                    className="max-w-4xl">
                            {studentsLoading ? (
                                <div className="flex justify-center items-center py-8">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-academy-600"></div>
                                    <span className="ml-3 text-ink-600">Đang tải danh sách học sinh...</span>
                                </div>
                            ) : (
                                <div className="space-y-6">
                                    {/* Header with Add Student Button */}
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <h4 className="text-md font-semibold text-ink-900">
                                                Học sinh trong lớp ({classStudents.length}/{selectedClass.capacity || 0})
                                            </h4>
                                            <p className="text-sm text-ink-600">Quản lý danh sách học sinh đã đăng ký lớp học này</p>
                                        </div>
                                        <button
                                            onClick={() => setShowAddStudentModal(true)}
                                            className="bg-academy-600 hover:bg-academy-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors duration-200 text-sm font-medium"
                                        >
                                            <Plus className="w-4 h-4" />
                                            <span>Thêm học sinh</span>
                                        </button>
                                    </div>

                                    {/* Search and Actions Bar */}
                                    {classStudents.length > 0 && (
                                        <div className="space-y-4">
                                            {/* Search Input */}
                                            <div className="relative">
                                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-ink-400 w-4 h-4" />
                                                <input
                                                    type="text"
                                                    placeholder="Tìm kiếm học sinh theo tên"
                                                    value={classStudentsSearchTerm}
                                                    onChange={(e) => setClassStudentsSearchTerm(e.target.value)}
                                                    className="w-full pl-10 pr-4 py-2 border border-ink-300 rounded-lg focus:ring-2 focus:ring-academy-200 focus:border-transparent"
                                                />
                                            </div>

                                            {/* Multi-select Actions */}
                                            <div className="flex items-center justify-between p-3 bg-ink-50 rounded-lg">
                                                <div className="flex items-center space-x-3">
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedClassStudentIds.length === classStudents.filter(student => {
                                                            return matchesStudentSearch(student, classStudentsSearchTerm);
                                                        }).length && classStudents.length > 0}
                                                        onChange={handleSelectAllClassStudents}
                                                        className="w-4 h-4 text-academy-600 bg-ink-100 border-ink-300 rounded focus:ring-academy-200"
                                                    />
                                                    <span className="text-sm font-medium text-ink-700">
                                                        {selectedClassStudentIds.length === classStudents.filter(student => {
                                                            return matchesStudentSearch(student, classStudentsSearchTerm);
                                                        }).length && classStudents.length > 0
                                                            ? 'Bỏ chọn tất cả'
                                                            : 'Chọn tất cả'}
                                                    </span>
                                                </div>
                                                <div className="flex items-center space-x-2">
                                                    <span className="text-sm text-ink-600">
                                                        Đã chọn: {selectedClassStudentIds.length}
                                                    </span>
                                                    {selectedClassStudentIds.length > 0 && (
                                                        <button
                                                            onClick={handleRemoveMultipleStudents}
                                                            disabled={isRemovingMultiple}
                                                            className="bg-danger-600 hover:bg-danger-700 disabled:bg-ink-400 text-white px-3 py-1 rounded text-sm transition-colors duration-200 flex items-center space-x-1"
                                                        >
                                                            {isRemovingMultiple ? (
                                                                <>
                                                                    <div className="animate-spin rounded-full h-3 w-3 border border-white border-t-transparent"></div>
                                                                    <span>Đang xóa...</span>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <Trash2 className="w-3 h-3" />
                                                                    <span>Xóa {selectedClassStudentIds.length}</span>
                                                                </>
                                                            )}
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Students List */}
                                    {classStudents.length === 0 ? (
                                        <div className="text-center py-8">
                                            <Users className="w-16 h-16 text-ink-300 mx-auto mb-4" />
                                            <h3 className="text-lg font-medium text-ink-900 mb-2">Chưa có học sinh nào</h3>
                                            <p className="text-ink-600 mb-4">Lớp học này chưa có học sinh đăng ký.</p>
                                            <button
                                                onClick={() => setShowAddStudentModal(true)}
                                                className="bg-academy-600 hover:bg-academy-700 text-white px-4 py-2 rounded-lg transition-colors duration-200 text-sm font-medium"
                                            >
                                                Thêm học sinh đầu tiên
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="grid gap-4">
                                            {classStudents
                                                .filter(student => {
                                                    return matchesStudentSearch(student, classStudentsSearchTerm);
                                                })
                                                .map((student) => (
                                                    <div key={student.id} className={`rounded-lg p-4 flex items-center justify-between transition-all duration-200 ${selectedClassStudentIds.includes(student.id)
                                                        ? 'bg-academy-50 border-2 border-academy-200'
                                                        : 'bg-ink-50 border-2 border-transparent'
                                                        }`}>
                                                        <div className="flex items-center space-x-4">
                                                            <input
                                                                type="checkbox"
                                                                checked={selectedClassStudentIds.includes(student.id)}
                                                                onChange={() => handleSelectClassStudent(student.id)}
                                                                className="w-4 h-4 text-academy-600 bg-ink-100 border-ink-300 rounded focus:ring-academy-200"
                                                            />
                                                            <div className="w-10 h-10 bg-ink-100 rounded-full flex items-center justify-center">
                                                                <span className="text-ink-700 font-semibold text-sm">
                                                                    {student.name ? student.name.split(' ').map((n: string) => n[0]).join('').slice(0, 2) : 'HS'}
                                                                </span>
                                                            </div>
                                                            <div>
                                                                <h5 className="font-medium text-ink-900">{student.name || 'Chưa có tên'}</h5>
                                                                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-ink-500">
                                                                    {student.phone && (
                                                                        <span className="inline-flex items-center gap-1">
                                                                            <Phone className="h-3.5 w-3.5" />
                                                                            {student.phone}
                                                                        </span>
                                                                    )}
                                                                    {student.email && (
                                                                        <span className="inline-flex items-center gap-1">
                                                                            <Mail className="h-3.5 w-3.5" />
                                                                            {student.email}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                <p className="text-xs text-ink-500">
                                                                    Đăng ký: {new Date(student.enrolledAt).toLocaleDateString('vi-VN')}
                                                                </p>
                                                            </div>
                                                        </div>
                                                        <button
                                                            onClick={() => handleRemoveStudent(student.id)}
                                                            className="text-danger-600 hover:text-danger-700 hover:bg-danger-50 p-2 rounded-lg transition-colors duration-200"
                                                            title="Xóa khỏi lớp"
                                                        >
                                                            <Trash2 className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                ))}

                                            {/* No results message when searching */}
                                            {classStudentsSearchTerm && classStudents.filter(student => {
                                                return matchesStudentSearch(student, classStudentsSearchTerm);
                                            }).length === 0 && (
                                                    <div className="text-center py-8">
                                                        <Search className="w-16 h-16 text-ink-300 mx-auto mb-4" />
                                                        <h3 className="text-lg font-medium text-ink-900 mb-2">Không tìm thấy học sinh</h3>
                                                        <p className="text-ink-600">Không có học sinh nào khớp với từ khóa "{classStudentsSearchTerm}"</p>
                                                    </div>
                                                )}
                                        </div>
                                    )}
                                </div>
                            )}
                </Modal>
            )}

            {/* Add Student Modal */}
            {showAddStudentModal && (
                <Modal
                    open={showAddStudentModal}
                    title="Thêm học sinh vào lớp"
                    onClose={closeAddStudentModal}
                    className="sm:max-w-3xl"
                    footer={
                        availableStudentsForEnrollment.length > 0 ? (
                            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                                <div className="text-sm text-ink-600">
                                    {selectedStudentIds.length > 0 ? <span>Đã chọn {selectedStudentIds.length} học sinh</span> : null}
                                </div>
                                <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                                    <Button variant="secondary" onClick={closeAddStudentModal} disabled={isEnrollingMultiple}>
                                        Hủy
                                    </Button>
                                    <Button
                                        onClick={handleEnrollMultipleStudents}
                                        disabled={selectedStudentIds.length === 0}
                                        loading={isEnrollingMultiple}
                                        icon={<Users className="h-4 w-4" />}
                                    >
                                        {isEnrollingMultiple ? 'Đang thêm...' : `Thêm ${selectedStudentIds.length} học sinh`}
                                    </Button>
                                </div>
                            </div>
                        ) : undefined
                    }
                >
                            <div className="mb-4">
                                <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                                    <div>
                                        <h4 className="mb-1 font-semibold text-ink-900">Chọn học sinh để thêm vào lớp</h4>
                                        <p className="text-sm text-ink-500">Có thể chọn nhiều học sinh cùng lúc</p>
                                    </div>
                                    <Button
                                        variant="secondary"
                                        icon={<Upload className="h-4 w-4" />}
                                        onClick={() => {
                                            resetStudentImport();
                                            setShowStudentImportModal(true);
                                        }}
                                    >
                                        Nhập từ Excel
                                    </Button>
                                </div>

                                {/* Search Input */}
                                <Input
                                    placeholder="Tìm kiếm học sinh theo tên hoặc dán danh sách..."
                                    value={studentSearchTerm}
                                    onChange={(e) => handleStudentSearchChange(e.target.value)}
                                    onPaste={(e) => {
                                        const pastedText = e.clipboardData.getData('text');
                                        if (handleStudentSearchPaste(pastedText)) {
                                            e.preventDefault();
                                        }
                                    }}
                                    leftIcon={<Search className="h-4 w-4" />}
                                    className="mb-2"
                                />

                                {/* Quick Instructions & Toggle */}
                                <div className="mb-4">
                                    <button
                                        type="button"
                                        onClick={() => setShowPasteInstructions(!showPasteInstructions)}
                                        className="flex cursor-pointer items-center gap-2 text-sm font-medium text-academy-700 transition-colors duration-200 hover:text-ink-900"
                                    >
                                        <AlertCircle className="h-4 w-4" />
                                        <span>Mẹo: Dán nhanh danh sách học sinh</span>
                                        <span className={`transform transition-transform ${showPasteInstructions ? 'rotate-180' : ''}`}>
                                            ▼
                                        </span>
                                    </button>

                                    {/* Expandable Usage Instructions */}
                                    {showPasteInstructions && (
                                        <div className="mt-2 p-3 bg-academy-50 border border-academy-200 rounded-lg">
                                            <div className="text-sm">
                                                <p className="text-academy-700 mb-2">
                                                    Copy danh sách tên và dán vào ô tìm kiếm để tự động tìm nhiều học sinh cùng lúc.
                                                </p>
                                                <p className="text-academy-600 text-xs">
                                                    <span className="font-medium">Hỗ trợ phân tách:</span> Xuống dòng • Dấu phẩy (,) • Dấu chấm phẩy (;) • Tab • Dấu gạch dọc (|)
                                                </p>
                                                <p className="text-academy-600 text-xs mt-1">
                                                    <span className="font-medium">Ví dụ:</span> "Nguyễn Văn A, Trần Thị B; Lê Hoàng C"
                                                </p>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Show paste results notification */}
                                {foundStudentsFromPaste.length > 0 && (
                                    <div className="mb-4 space-y-3">
                                        {/* Success notification */}
                                        <div className="p-3 bg-success-50 border border-success-200 rounded-lg">
                                            <div className="flex items-center space-x-2">
                                                <CheckCircle className="w-5 h-5 text-success-600" />
                                                <div className="flex-1">
                                                    <p className="text-sm font-medium text-success-800">
                                                        Đã tìm thấy {foundStudentsFromPaste.length} học sinh từ bộ nhớ tạm
                                                    </p>
                                                    {foundStudentsFromPaste[0]?._separatorInfo && (
                                                        <p className="text-xs text-success-600 mt-1">
                                                            Phân tách bằng <span className="font-medium">{foundStudentsFromPaste[0]._separatorInfo.separator}</span>
                                                            {' '}({foundStudentsFromPaste[0]._separatorInfo.totalNames} tên → {foundStudentsFromPaste.length} tìm thấy)
                                                        </p>
                                                    )}
                                                    <p className="text-xs text-success-600 mt-1">
                                                        Các học sinh đã được tự động chọn. Bạn có thể bỏ chọn hoặc thêm thêm học sinh khác.
                                                    </p>
                                                </div>
                                                <button
                                                    onClick={() => setFoundStudentsFromPaste([])}
                                                    className="text-success-600 hover:text-success-800 transition-colors"
                                                >
                                                    <X className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </div>

                                        {/* Error notifications */}
                                        {foundStudentsFromPaste[0]?._separatorInfo && (
                                            <>
                                                {/* Students already in class */}
                                                {foundStudentsFromPaste[0]._separatorInfo.alreadyInClassNames.length > 0 && (
                                                    <div className="p-3 bg-warning-50 border border-warning-200 rounded-lg">
                                                        <div className="flex items-start space-x-2">
                                                            <AlertTriangle className="w-5 h-5 text-warning-600 flex-shrink-0 mt-0.5" />
                                                            <div className="flex-1">
                                                                <p className="text-sm font-medium text-warning-800">
                                                                    {foundStudentsFromPaste[0]._separatorInfo.alreadyInClassNames.length} học sinh đã có trong lớp
                                                                </p>
                                                                <p className="text-xs text-warning-700 mt-1">
                                                                    <span className="font-medium">Danh sách:</span> {foundStudentsFromPaste[0]._separatorInfo.alreadyInClassNames.join(', ')}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Students not found */}
                                                {foundStudentsFromPaste[0]._separatorInfo.notFoundNames.length > 0 && (
                                                    <div className="p-3 bg-danger-50 border border-danger-200 rounded-lg">
                                                        <div className="flex items-start space-x-2">
                                                            <XCircle className="w-5 h-5 text-danger-600 flex-shrink-0 mt-0.5" />
                                                            <div className="flex-1">
                                                                <p className="text-sm font-medium text-danger-800">
                                                                    {foundStudentsFromPaste[0]._separatorInfo.notFoundNames.length} học sinh không tìm thấy
                                                                </p>
                                                                <p className="text-xs text-danger-700 mt-1">
                                                                    <span className="font-medium">Danh sách:</span> {foundStudentsFromPaste[0]._separatorInfo.notFoundNames.join(', ')}
                                                                </p>
                                                                <p className="text-xs text-danger-600 mt-1">
                                                                    Có thể do tên không khớp hoặc học sinh chưa có trong hệ thống.
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>

                            {availableStudentsForEnrollment.length > 0 && (
                                <div className="mb-4 flex items-center justify-between rounded-lg border border-ink-200 bg-ink-50 p-3">
                                    <div className="flex items-center gap-3">
                                        <input
                                            type="checkbox"
                                            checked={selectedStudentIds.length === availableStudentsForEnrollment.length}
                                            onChange={handleSelectAllStudents}
                                            className="h-4 w-4 rounded border-ink-200 text-academy-600 focus:ring-academy-200"
                                        />
                                        <span className="text-sm font-medium text-ink-700">
                                            {selectedStudentIds.length === availableStudentsForEnrollment.length
                                                ? 'Bỏ chọn tất cả'
                                                : 'Chọn tất cả'}
                                        </span>
                                    </div>
                                    <span className="text-sm text-ink-500">
                                        Đã chọn: {selectedStudentIds.length} / {availableStudentsForEnrollment.length}
                                    </span>
                                </div>
                            )}

                            {/* Available Students List */}
                            <div className="space-y-3 max-h-96 overflow-y-auto">
                                {(() => {
                                    // Filter available students (not in class)
                                    const availableStudents = allStudents.filter(student =>
                                        !classStudents.some(cs => cs.id === student.id)
                                    );

                                    // If we have paste results, prioritize them
                                    if (foundStudentsFromPaste.length > 0) {
                                        // Show paste results first, then other available students
                                        const pasteStudentIds = foundStudentsFromPaste.map(s => s.id);
                                        const otherStudents = availableStudents.filter(student =>
                                            !pasteStudentIds.includes(student.id)
                                        );

                                        return [
                                            ...foundStudentsFromPaste,
                                            ...otherStudents.filter(student => {
                                                return matchesStudentSearch(student, studentSearchTerm);
                                            })
                                        ];
                                    }

                                    // Normal search filtering
                                    return availableStudents.filter(student => {
                                        return matchesStudentSearch(student, studentSearchTerm);
                                    });
                                })()
                                    .map((student) => {
                                        const isFromPaste = foundStudentsFromPaste.some(p => p.id === student.id);
                                        const isSelected = selectedStudentIds.includes(student.id);

                                        return (
                                            <div
                                                key={student.id}
                                                className={`cursor-pointer rounded-lg border p-4 transition-colors duration-200 ${isSelected
                                                    ? 'border-academy-500 bg-academy-50'
                                                    : isFromPaste
                                                        ? 'border-success-100 bg-success-50 hover:bg-action-50'
                                                        : 'border-ink-200 bg-white hover:bg-ink-50'
                                                    }`}
                                                onClick={() => handleSelectStudent(student.id)}
                                            >
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center space-x-3">
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedStudentIds.includes(student.id)}
                                                            onChange={() => handleSelectStudent(student.id)}
                                                            className="h-4 w-4 rounded border-ink-200 text-academy-600 focus:ring-academy-200"
                                                            onClick={(e) => e.stopPropagation()}
                                                        />
                                                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-ink-100">
                                                            <span className="text-ink-700 font-semibold text-sm">
                                                                {(student.name || '').split(' ').map((n: string) => n[0]).join('').slice(0, 2)}
                                                            </span>
                                                        </div>
                                                        <div>
                                                            <div className="flex items-center space-x-2">
                                                                <h5 className="font-medium text-ink-900">{student.name}</h5>
                                                                {isFromPaste && (
                                                                    <span className="rounded bg-success-100 px-2 py-0.5 text-xs font-medium text-success-700">
                                                                        Từ bộ nhớ tạm
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-ink-500">
                                                                {student.phone && (
                                                                    <span className="inline-flex items-center gap-1">
                                                                        <Phone className="h-3.5 w-3.5" />
                                                                        {student.phone}
                                                                    </span>
                                                                )}
                                                                {student.email && student.phone && <span> • </span>}
                                                                {student.email && (
                                                                    <span className="inline-flex items-center gap-1">
                                                                        <Mail className="h-3.5 w-3.5" />
                                                                        {student.email}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <Button
                                                        type="button"
                                                        variant="secondary"
                                                        size="sm"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleEnrollStudent(student.id);
                                                        }}
                                                    >
                                                        Thêm riêng
                                                    </Button>
                                                </div>
                                            </div>
                                        );
                                    })}

                                {availableStudentsForEnrollment.length === 0 && (
                                    <div className="text-center py-8">
                                        <Users className="w-16 h-16 text-ink-300 mx-auto mb-4" />
                                        <h3 className="text-lg font-medium text-ink-900 mb-2">Không có học sinh khả dụng</h3>
                                        <p className="text-ink-600">Tất cả học sinh đã đăng ký lớp này hoặc chưa có học sinh nào trong hệ thống.</p>
                                    </div>
                                )}
                            </div>
                </Modal>
            )}

            {/* Settings Modal */}
            {showSettingsModal && selectedClass && (
                <Modal
                    open={showSettingsModal}
                    title="Cài đặt lớp học"
                    onClose={() => setShowSettingsModal(false)}
                    className="max-w-md"
                    footer={
                        <div className="flex justify-end space-x-3">
                    <button
                        onClick={() => {
                            setShowSettingsModal(false);
                            notify('✅ Đã lưu cài đặt!', 'success');
                        }}
                        className="px-4 py-2 bg-academy-600 hover:bg-academy-700 text-white rounded-lg transition-colors duration-200 text-sm font-medium"
                    >
                        Lưu cài đặt
                    </button>
                        </div>
                    }>
                            <div className="space-y-4">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <h4 className="text-sm font-medium text-ink-900">Thông báo điểm danh</h4>
                                        <p className="text-xs text-ink-500">Gửi email khi có học sinh vắng mặt</p>
                                    </div>
                                    <button className="relative inline-flex h-6 w-11 items-center rounded-full bg-academy-600 transition-colors">
                                        <span className="sr-only">Enable notifications</span>
                                        <span className="inline-block h-4 w-4 transform rounded-full bg-white transition-transform translate-x-6" />
                                    </button>
                                </div>

                                <div className="flex items-center justify-between">
                                    <div>
                                        <h4 className="text-sm font-medium text-ink-900">Sao lưu tự động</h4>
                                        <p className="text-xs text-ink-500">Sao lưu dữ liệu lớp học hàng tuần</p>
                                    </div>
                                    <button className="relative inline-flex h-6 w-11 items-center rounded-full bg-ink-200 transition-colors">
                                        <span className="sr-only">Enable auto backup</span>
                                        <span className="inline-block h-4 w-4 transform rounded-full bg-white transition-transform translate-x-1" />
                                    </button>
                                </div>

                                <div className="flex items-center justify-between">
                                    <div>
                                        <h4 className="text-sm font-medium text-ink-900">Báo cáo tuần</h4>
                                        <p className="text-xs text-ink-500">Gửi báo cáo hoạt động hàng tuần</p>
                                    </div>
                                    <button className="relative inline-flex h-6 w-11 items-center rounded-full bg-academy-600 transition-colors">
                                        <span className="sr-only">Enable weekly reports</span>
                                        <span className="inline-block h-4 w-4 transform rounded-full bg-white transition-transform translate-x-6" />
                                    </button>
                                </div>
                            </div>
                </Modal>
            )}

            {/* Import Modal */}
            {showImportModal && (
                <Modal
                    open={showImportModal}
                    title="Nhập danh sách lớp học"
                    onClose={() => setShowImportModal(false)}
                    className="max-w-4xl">
                            {!showPreview && !importResults ? (
                                <div className="space-y-6">
                                    {/* Method Selection */}
                                    <div>
                                        <h4 className="text-md font-semibold text-ink-900 mb-4">Chọn phương thức nhập</h4>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <button
                                                onClick={() => setImportMethod('file')}
                                                className={`p-4 rounded-lg border-2 transition-all duration-200 ${importMethod === 'file'
                                                    ? 'border-academy-500 bg-academy-50'
                                                    : 'border-ink-200 hover:border-ink-300'
                                                    }`}
                                            >
                                                <div className="flex items-center space-x-3">
                                                    <FileSpreadsheet className="w-8 h-8 text-academy-600" />
                                                    <div className="text-left">
                                                        <h5 className="font-medium text-ink-900">Tải file</h5>
                                                        <p className="text-sm text-ink-600">CSV, Excel, JSON</p>
                                                    </div>
                                                </div>
                                            </button>

                                            <button
                                                onClick={() => setImportMethod('googlesheets')}
                                                className={`p-4 rounded-lg border-2 transition-all duration-200 ${importMethod === 'googlesheets'
                                                    ? 'border-academy-500 bg-academy-50'
                                                    : 'border-ink-200 hover:border-ink-300'
                                                    }`}
                                            >
                                                <div className="flex items-center space-x-3">
                                                    <Link className="w-8 h-8 text-ink-500" />
                                                    <div className="text-left">
                                                        <h5 className="font-medium text-ink-900">Google Sheets</h5>
                                                        <p className="text-sm text-ink-600">Nhập từ link</p>
                                                    </div>
                                                </div>
                                            </button>
                                        </div>
                                    </div>

                                    {/* Template Download */}
                                    <div className="bg-ink-50 border border-ink-200 rounded-lg p-4">
                                        <div className="flex items-start space-x-3">
                                            <Download className="w-5 h-5 text-ink-500 mt-0.5" />
                                            <div className="flex-1">
                                                <h5 className="font-medium text-ink-900">Tải file mẫu</h5>
                                                <p className="text-sm text-ink-600 mb-3">
                                                    Tải file mẫu để tham khảo định dạng và cách điền thông tin
                                                </p>
                                                <button
                                                    onClick={downloadTemplate}
                                                    className="bg-academy-600 hover:bg-academy-700 text-white px-4 py-2 rounded-lg text-sm transition-colors duration-200"
                                                >
                                                    Tải file mẫu
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                    {/* File Upload */}
                                    {importMethod === 'file' && (
                                        <div>
                                            <label className="block text-sm font-medium text-ink-700 mb-2">
                                                Chọn file để nhập
                                            </label>
                                            <div
                                                className={`mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-dashed rounded-md transition-all duration-200 ${isDragOver
                                                    ? 'border-academy-500 bg-academy-50'
                                                    : 'border-ink-300 hover:border-ink-400'
                                                    }`}
                                                onDrop={handleDrop}
                                                onDragOver={handleDragOver}
                                                onDragEnter={handleDragEnter}
                                                onDragLeave={handleDragLeave}
                                            >
                                                <div className="space-y-1 text-center">
                                                    <Upload className={`mx-auto h-12 w-12 transition-colors duration-200 ${isDragOver ? 'text-academy-500' : 'text-ink-400'
                                                        }`} />
                                                    <div className="flex text-sm text-ink-600">
                                                        <label className="relative cursor-pointer bg-white rounded-md font-medium text-academy-600 hover:text-academy-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-academy-200">
                                                            <span>Tải file lên</span>
                                                            <input
                                                                type="file"
                                                                className="sr-only"
                                                                accept=".csv,.json,.xlsx,.xls"
                                                                onChange={handleFileChange}
                                                            />
                                                        </label>
                                                        <p className="pl-1">hoặc kéo thả</p>
                                                    </div>
                                                    <p className="text-xs text-ink-500">
                                                        CSV, JSON, XLSX, XLS tối đa 10MB
                                                    </p>
                                                    {isDragOver && (
                                                        <p className="text-sm text-academy-600 font-medium">
                                                            Thả file vào đây để tải lên
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                            {file && (
                                                <div className="mt-2 text-sm text-ink-600">
                                                    File đã chọn: {file.name}
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Google Sheets URL */}
                                    {importMethod === 'googlesheets' && (
                                        <div>
                                            <label className="block text-sm font-medium text-ink-700 mb-2">
                                                Link Google Sheets
                                            </label>
                                            <div className="space-y-3">
                                                <input
                                                    type="url"
                                                    value={googleSheetsUrl}
                                                    onChange={(e) => setGoogleSheetsUrl(e.target.value)}
                                                    placeholder="https://docs.google.com/spreadsheets/d/..."
                                                    className="min-h-10 w-full rounded-lg border border-ink-300 bg-white px-3 py-2 text-sm text-ink-900 focus:border-academy-500 focus:outline-none focus:ring-2 focus:ring-academy-100"
                                                />
                                                <div className="bg-warning-50 border border-warning-200 rounded-lg p-3">
                                                    <div className="flex items-start space-x-2">
                                                        <AlertTriangle className="w-4 h-4 text-warning-600 mt-0.5" />
                                                        <div className="text-sm text-warning-700">
                                                            <strong>Lưu ý:</strong> Google Sheets phải được chia sẻ công khai để có thể truy cập.
                                                        </div>
                                                    </div>
                                                </div>
                                                <button
                                                    onClick={handleGoogleSheetsUrl}
                                                    disabled={!googleSheetsUrl.trim()}
                                                    className="w-full bg-academy-600 hover:bg-academy-700 disabled:bg-ink-400 text-white px-4 py-2 rounded-lg transition-colors duration-200 text-sm font-medium"
                                                >
                                                    Tải dữ liệu từ Google Sheets
                                                </button>
                                            </div>
                                        </div>
                                    )}

                                    {/* Column Mapping Info */}
                                    <div className="bg-ink-50 border border-ink-200 rounded-lg p-4">
                                        <h5 className="font-medium text-ink-900 mb-2">Hỗ trợ các cột:</h5>
                                        <div className="grid grid-cols-2 gap-2 text-sm text-ink-700">
                                            <div>• Tên lớp (name)</div>
                                            <div>• Môn học (subject)</div>
                                            <div>• Cơ sở (campusName)</div>
                                            <div>• Phòng (room)</div>
                                            <div>• Giáo viên (teacher)</div>
                                            <div>• Trợ giảng (assistant)</div>
                                            <div>• Học phí (fee)</div>
                                            <div>• Mô tả (description)</div>
                                            <div>• Ngày bắt đầu (startDate)</div>
                                            <div>• Ngày kết thúc (endDate)</div>
                                            <div>• Sức chứa (capacity)</div>
                                            <div>• Lịch học (schedule)</div>
                                        </div>
                                    </div>
                                </div>
                            ) : showPreview && !importResults ? (
                                /* Preview Section */
                                <div className="space-y-6">
                                    <div className="flex items-center justify-between">
                                        <h4 className="text-md font-semibold text-ink-900">
                                            Xem trước dữ liệu ({importData.length} lớp học)
                                        </h4>
                                        <button
                                            onClick={() => {
                                                setShowPreview(false);
                                                setImportData([]);
                                            }}
                                            className="text-ink-600 hover:text-ink-800 transition-colors duration-200"
                                        >
                                            Quay lại
                                        </button>
                                    </div>

                                    {/* Preview Table */}
                                    <div className="border border-ink-200 rounded-lg overflow-hidden">
                                        <div className="overflow-x-auto max-h-96">
                                            <table className="min-w-full divide-y divide-ink-200">
                                                <thead>
                                                    <tr>
                                                        <th className="px-6 py-3 text-left text-xs font-medium text-ink-500">Tên lớp</th>
                                                        <th className="px-6 py-3 text-left text-xs font-medium text-ink-500">Môn học</th>
                                                        <th className="px-6 py-3 text-left text-xs font-medium text-ink-500">Cơ sở</th>
                                                        <th className="px-6 py-3 text-left text-xs font-medium text-ink-500">Phòng</th>
                                                        <th className="px-6 py-3 text-left text-xs font-medium text-ink-500">Thời gian</th>
                                                        <th className="px-6 py-3 text-left text-xs font-medium text-ink-500">Lịch học</th>
                                                        <th className="px-6 py-3 text-left text-xs font-medium text-ink-500">Sức chứa</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="bg-white divide-y divide-ink-200">
                                                    {importData.slice(0, 10).map((item, index) => (
                                                        <tr key={index} className="hover:bg-ink-50">
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-ink-900">{item.name || '-'}</td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-ink-900">{item.subject || '-'}</td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-ink-900">{item.campusName || '-'}</td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-ink-900">{item.room || '-'}</td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-ink-900">
                                                                {item.startTime && item.endTime ? `${item.startTime} - ${item.endTime}` : '-'}
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-ink-900">{item.scheduleRaw || '-'}</td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-ink-900">{item.capacity || '-'}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                        {importData.length > 10 && (
                                            <div className="bg-ink-50 px-6 py-3 text-sm text-ink-500">
                                                Hiển thị 10 trong số {importData.length} lớp học
                                            </div>
                                        )}
                                    </div>

                                    {/* Import Button */}
                                    <div className="flex justify-end space-x-3">
                                        <button
                                            onClick={() => {
                                                setShowPreview(false);
                                                setImportData([]);
                                            }}
                                            className="px-4 py-2 text-ink-700 bg-ink-200 hover:bg-ink-300 rounded-lg transition-colors duration-200"
                                        >
                                            Hủy
                                        </button>
                                        <button
                                            onClick={handleImport}
                                            disabled={isImporting || importData.length === 0}
                                            className="px-6 py-2 bg-academy-600 hover:bg-academy-700 disabled:bg-academy-300 text-white rounded-lg transition-colors duration-200 flex items-center space-x-2 text-sm font-medium"
                                        >
                                            {isImporting && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>}
                                            <span>{isImporting ? 'Đang nhập...' : `Nhập ${importData.length} lớp học`}</span>
                                        </button>
                                    </div>
                                </div>
                            ) : importResults ? (
                                /* Results Section */
                                <div className="space-y-6">
                                    <div className="text-center">
                                        <CheckCircle className="w-16 h-16 text-success-500 mx-auto mb-4" />
                                        <h4 className="text-lg font-semibold text-ink-900 mb-2">Nhập hoàn thành!</h4>
                                        <p className="text-ink-600">
                                            Đã xử lý {importResults.total} lớp học: {importResults.success.length} thành công, {importResults.errors.length} lỗi
                                        </p>
                                    </div>

                                    {/* Success Results */}
                                    {importResults.success.length > 0 && (
                                        <div>
                                            <h5 className="font-medium text-success-700 mb-3">✅ Thành công ({importResults.success.length})</h5>
                                            <div className="bg-success-50 border border-success-200 rounded-lg p-4 max-h-48 overflow-y-auto">
                                                <div className="space-y-2">
                                                    {importResults.success.map((item: any, index: number) => (
                                                        <div key={index} className="text-sm text-success-700">
                                                            Dòng {item.row}: {item.name} - {item.subject} ({item.campus})
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Error Results */}
                                    {importResults.errors.length > 0 && (
                                        <div>
                                            <h5 className="font-medium text-danger-700 mb-3">❌ Lỗi ({importResults.errors.length})</h5>
                                            <div className="bg-danger-50 border border-danger-200 rounded-lg p-4 max-h-48 overflow-y-auto">
                                                <div className="space-y-2">
                                                    {importResults.errors.map((error: any, index: number) => (
                                                        <div key={index} className="text-sm text-danger-700">
                                                            <strong>Dòng {error.row}:</strong> {error.error}
                                                            {error.data?.name && <span className="block text-danger-600 ml-4">Tên: {error.data.name}</span>}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Close Button */}
                                    <div className="flex justify-center">
                                        <button
                                            onClick={() => {
                                                resetImport();
                                                setShowImportModal(false);
                                            }}
                                            className="px-6 py-2 bg-academy-600 hover:bg-academy-700 text-white rounded-lg transition-colors duration-200 text-sm font-medium"
                                        >
                                            Đóng
                                        </button>
                                    </div>
                                </div>
                            ) : null}
                </Modal>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteModal && classToDelete && (
                <Modal
                    open={showDeleteModal}
                    title="⚠️ Xác nhận xóa lớp học"
                    onClose={handleCancelDelete}
                    className="max-w-md"
                    footer={
                        <div className="flex justify-end space-x-3">
                    <button
                        onClick={handleCancelDelete}
                        disabled={isDeleting}
                        className="px-4 py-2 text-ink-700 bg-ink-200 hover:bg-ink-300 rounded-lg transition-colors duration-200 disabled:opacity-50"
                    >
                        Hủy
                    </button>
                    <button
                        onClick={handleConfirmDelete}
                        disabled={isDeleting}
                        className="px-4 py-2 bg-danger-600 hover:bg-danger-700 disabled:bg-danger-400 text-white rounded-lg transition-colors duration-200 flex items-center space-x-2"
                    >
                        {isDeleting ? (
                            <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                <span>Đang xóa...</span>
                            </>
                        ) : (
                            <>
                                <Trash2 className="w-4 h-4" />
                                <span>Xác nhận xóa</span>
                            </>
                        )}
                    </button>
                        </div>
                    }>
                            <div className="space-y-4">
                                <div className="flex items-center space-x-3">
                                    <div className="w-12 h-12 bg-danger-100 rounded-full flex items-center justify-center">
                                        <AlertCircle className="w-6 h-6 text-danger-600" />
                                    </div>
                                    <div className="flex-1">
                                        <h4 className="font-medium text-ink-900">Bạn có chắc chắn muốn xóa lớp học này?</h4>
                                        <p className="text-sm text-ink-600 mt-1">Hành động này không thể hoàn tác!</p>
                                    </div>
                                </div>

                                <div className="bg-ink-50 rounded-lg p-4">
                                    <h5 className="font-medium text-ink-900 mb-2">Thông tin lớp học sẽ bị xóa:</h5>
                                    <div className="space-y-1 text-sm text-ink-600">
                                        <p><span className="font-medium">Tên lớp:</span> {classToDelete.name}</p>
                                        <p><span className="font-medium">Môn học:</span> {typeof classToDelete.subject === 'object' ? classToDelete.subject?.name : classToDelete.subject}</p>
                                        <p><span className="font-medium">Cơ sở:</span> {getCampusDisplayName(classToDelete.campus)}</p>
                                        <p><span className="font-medium">Giáo viên:</span> {classToDelete.teacher}</p>
                                        <p><span className="font-medium">Học sinh:</span> {classToDelete.enrolled || 0} học sinh</p>
                                    </div>
                                </div>

                                <div className="bg-danger-50 border border-danger-200 rounded-lg p-4">
                                    <div className="flex items-start space-x-2">
                                        <AlertCircle className="w-5 h-5 text-danger-600 mt-0.5" />
                                        <div className="text-sm text-danger-800">
                                            <p className="font-medium">Lưu ý quan trọng:</p>
                                            <ul className="mt-1 space-y-1">
                                                <li>• Tất cả học sinh sẽ bị rút khỏi lớp học</li>
                                                <li>• Lịch sử điểm danh và bài tập sẽ bị mất</li>
                                                <li>• Dữ liệu không thể khôi phục sau khi xóa</li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </div>
                </Modal>
            )}

            {/* Student Removal Confirmation Modal */}
            {showRemoveStudentModal && studentToRemove && (
                <Modal
                    open={showRemoveStudentModal}
                    title="Xác nhận xóa học sinh khỏi lớp"
                    onClose={() => {
                            setShowRemoveStudentModal(false);
                            setStudentToRemove(null);
                        }}
                    className="max-w-md"
                    footer={
                        <div className="flex justify-end space-x-3">
                        <button
                            type="button"
                            onClick={() => {
                                setShowRemoveStudentModal(false);
                                setStudentToRemove(null);
                            }}
                            className="px-4 py-2 text-ink-700 bg-ink-100 hover:bg-ink-200 rounded-lg transition-colors duration-200"
                            disabled={isRemovingStudent}
                        >
                            Hủy
                        </button>
                        <button
                            onClick={handleConfirmRemoveStudent}
                            disabled={isRemovingStudent}
                            className="px-4 py-2 bg-danger-600 hover:bg-danger-700 text-white rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                        >
                            {isRemovingStudent ? (
                                <>
                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                    <span>Đang xóa...</span>
                                </>
                            ) : (
                                <>
                                    <Trash2 className="w-4 h-4" />
                                    <span>Xóa khỏi lớp</span>
                                </>
                            )}
                        </button>
                        </div>
                    }>

                            <div className="space-y-4">
                                <div className="bg-danger-50 p-4 rounded-lg">
                                    <div className="space-y-2">
                                        <p className="text-sm text-danger-800">
                                            <strong>Học sinh:</strong> {studentToRemove.name}
                                        </p>
                                        <p className="text-sm text-danger-800">
                                            <strong>Lớp học:</strong> {selectedClass.name}
                                        </p>
                                        <p className="text-sm text-danger-800">
                                            <strong>Email:</strong> {studentToRemove.email || 'Chưa có'}
                                        </p>
                                        <p className="text-sm text-danger-800">
                                            <strong>Điện thoại:</strong> {studentToRemove.phone || 'Chưa có'}
                                        </p>
                                        <p className="text-sm text-danger-800">
                                            <strong>Ngày đăng ký:</strong> {new Date(studentToRemove.enrolledAt).toLocaleDateString('vi-VN')}
                                        </p>
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <p className="text-sm text-ink-600">
                                        Bạn có chắc chắn muốn xóa học sinh này khỏi lớp học không?
                                    </p>
                                    <div className="bg-warning-50 p-3 rounded-lg">
                                        <p className="text-sm text-warning-800">
                                            <strong>⚠️ Lưu ý:</strong> Việc xóa học sinh khỏi lớp sẽ ảnh hưởng đến:
                                        </p>
                                        <ul className="text-sm text-warning-800 mt-1 space-y-1">
                                            <li>• Lịch sử điểm danh của học sinh</li>
                                            <li>• Thống kê lớp học và số lượng học sinh</li>
                                            <li>• Học sinh có thể đăng ký lại lớp này sau</li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                </Modal>
            )}

            {/* Exam Management Modal */}
            {showExamsModal && selectedClass && (
                <Modal
                    open={showExamsModal}
                    title={`Quản lý bài thi - ${selectedClass.name}`}
                    onClose={() => {
                            setShowExamsModal(false);
                            setClassExams([]);
                        }}
                    className="max-w-4xl">
                            {examsLoading ? (
                                <div className="flex justify-center items-center py-8">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-academy-600"></div>
                                    <span className="ml-3 text-ink-600">Đang tải danh sách bài thi...</span>
                                </div>
                            ) : (
                                <div className="space-y-6">
                                    {/* Header with Add Exam Button */}
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <h4 className="text-md font-semibold text-ink-900">
                                                Danh sách bài thi ({classExams.length})
                                            </h4>
                                            <p className="text-sm text-ink-600">Quản lý các bài thi và kiểm tra của lớp học</p>
                                        </div>
                                        <button
                                            onClick={handleAddExam}
                                            className="bg-academy-600 hover:bg-academy-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors duration-200 text-sm font-medium"
                                        >
                                            <Plus className="w-4 h-4" />
                                            <span>Thêm bài thi</span>
                                        </button>
                                    </div>

                                    {/* Exams List */}
                                    {classExams.length === 0 ? (
                                        <div className="text-center py-8">
                                            <FileText className="w-16 h-16 text-ink-300 mx-auto mb-4" />
                                            <h3 className="text-lg font-medium text-ink-900 mb-2">Chưa có bài thi nào</h3>
                                            <p className="text-ink-600 mb-4">Lớp học này chưa có bài thi nào được tạo.</p>
                                            <button
                                                onClick={handleAddExam}
                                                className="bg-academy-600 hover:bg-academy-700 text-white px-4 py-2 rounded-lg transition-colors duration-200 text-sm font-medium"
                                            >
                                                Tạo bài thi đầu tiên
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="grid gap-4">
                                            {classExams.map((exam) => (
                                                <div key={exam.id} className="rounded-lg border border-ink-200 bg-white p-4">
                                                    <div className="flex items-start justify-between gap-3">
                                                        <div className="flex items-center space-x-3">
                                                            <div>
                                                                <h5 className="font-medium text-ink-900">{exam.name}</h5>
                                                                <div className="flex items-center space-x-4 text-sm text-ink-600">
                                                                    <span className="flex items-center">
                                                                        <Target className="w-4 h-4 mr-1" />
                                                                        Hệ số: {exam.coefficient}
                                                                    </span>
                                                                    <span className="flex items-center">
                                                                        <Calendar className="w-4 h-4 mr-1" />
                                                                        {new Date(exam.examDate).toLocaleDateString('vi-VN')}
                                                                    </span>
                                                                    <span className="flex items-center">
                                                                        <Clock className="w-4 h-4 mr-1" />
                                                                        {exam.duration} phút
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="flex items-center space-x-2">
                                                            <span className={`px-2 py-0.5 text-xs rounded ${exam.status === 'Đã hoàn thành' ? 'bg-success-100 text-success-800' :
                                                                exam.status === 'Đang thi' ? 'bg-warning-100 text-warning-800' :
                                                                    'bg-academy-50 text-academy-800'
                                                                }`}>
                                                                {exam.status}
                                                            </span>
                                                            {exam.hasScores && (
                                                                <span className="px-2 py-0.5 text-xs bg-academy-100 text-academy-800 rounded">
                                                                    {exam.scoredStudents}/{classStudents.length} điểm
                                                                </span>
                                                            )}
                                                            <button
                                                                onClick={() => handleManageScores(exam)}
                                                                className="text-academy-600 hover:text-academy-700 hover:bg-academy-50 p-1 rounded transition-colors duration-200"
                                                                title="Quản lý điểm số"
                                                            >
                                                                <Award className="w-4 h-4" />
                                                            </button>
                                                            <button
                                                                onClick={() => handleEditExam(exam)}
                                                                className="text-academy-600 hover:text-academy-700 hover:bg-ink-100 p-1 rounded transition-colors duration-200"
                                                                title="Chỉnh sửa"
                                                            >
                                                                <Edit2 className="w-4 h-4" />
                                                            </button>
                                                            <button
                                                                onClick={() => handleDeleteExam(exam)}
                                                                className="text-danger-600 hover:text-danger-700 hover:bg-danger-50 p-1 rounded transition-colors duration-200"
                                                                title="Xóa"
                                                            >
                                                                <Trash2 className="w-4 h-4" />
                                                            </button>
                                                        </div>
                                                    </div>

                                                    <dl className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-sm">
                                                        <div className="flex gap-1.5"><dt className="text-ink-500">Loại</dt><dd className="text-ink-900">{exam.examType}</dd></div>
                                                        <div className="flex gap-1.5"><dt className="text-ink-500">Điểm tối đa</dt><dd className="text-ink-900">{exam.maxScore}</dd></div>
                                                    </dl>

                                                    {exam.description && (
                                                        <p className="mt-2 text-sm text-ink-600">{exam.description}</p>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                </Modal>
            )}

            {/* Exam Form Modal */}
            {showExamForm && (
                <Modal
                    open={showExamForm}
                    title={selectedExam ? 'Chỉnh sửa bài thi' : 'Thêm bài thi mới'}
                    onClose={() => {
                            setShowExamForm(false);
                            setSelectedExam(null);
                        }}
                    className="max-w-2xl"
                    footer={
                        <div className="flex justify-end space-x-3">
                        <button
                            onClick={() => {
                                setShowExamForm(false);
                                setSelectedExam(null);
                            }}
                            className="px-4 py-2 text-ink-700 bg-ink-100 hover:bg-ink-200 rounded-lg transition-colors duration-200"
                            disabled={isExamSubmitting}
                        >
                            Hủy
                        </button>
                        <button
                            onClick={handleExamSubmit}
                            disabled={isExamSubmitting}
                            className="px-4 py-2 bg-academy-600 hover:bg-academy-700 text-white rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 text-sm font-medium"
                        >
                            {isExamSubmitting ? (
                                <>
                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                    <span>Đang lưu...</span>
                                </>
                            ) : (
                                <>
                                    <Award className="w-4 h-4" />
                                    <span>{selectedExam ? 'Cập nhật' : 'Thêm bài thi'}</span>
                                </>
                            )}
                        </button>
                        </div>
                    }>
                            <div className="space-y-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-ink-700 mb-1">
                                            Tên bài thi *
                                        </label>
                                        <input
                                            type="text"
                                            value={examFormData.name}
                                            onChange={(e) => {
                                                setExamFormData({ ...examFormData, name: e.target.value });
                                                if (examFormErrors.name) setExamFormErrors({ ...examFormErrors, name: '' });
                                            }}
                                            placeholder="Nhập tên bài thi"
                                            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-academy-200 focus:border-academy-500 ${examFormErrors.name ? 'border-danger-500' : 'border-ink-300'
                                                }`}
                                        />
                                        {examFormErrors.name && <p className="text-danger-500 text-sm mt-1">{examFormErrors.name}</p>}
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-ink-700 mb-1">
                                            Hệ số *
                                        </label>
                                        <input
                                            type="number"
                                            step="0.1"
                                            min="0.1"
                                            value={examFormData.coefficient}
                                            onChange={(e) => {
                                                setExamFormData({ ...examFormData, coefficient: e.target.value });
                                                if (examFormErrors.coefficient) setExamFormErrors({ ...examFormErrors, coefficient: '' });
                                            }}
                                            placeholder="1.0"
                                            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-academy-200 focus:border-academy-500 ${examFormErrors.coefficient ? 'border-danger-500' : 'border-ink-300'
                                                }`}
                                        />
                                        {examFormErrors.coefficient && <p className="text-danger-500 text-sm mt-1">{examFormErrors.coefficient}</p>}
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <DateInput
                                        label="Ngày thi *"
                                        value={examFormData.examDate}
                                        onChange={(value) => {
                                            setExamFormData({ ...examFormData, examDate: value });
                                            if (examFormErrors.examDate) setExamFormErrors({ ...examFormErrors, examDate: '' });
                                        }}
                                        error={examFormErrors.examDate}
                                    />

                                    <div>
                                        <label className="block text-sm font-medium text-ink-700 mb-1">
                                            Thời gian (phút) *
                                        </label>
                                        <input
                                            type="number"
                                            min="1"
                                            value={examFormData.duration}
                                            onChange={(e) => {
                                                setExamFormData({ ...examFormData, duration: e.target.value });
                                                if (examFormErrors.duration) setExamFormErrors({ ...examFormErrors, duration: '' });
                                            }}
                                            placeholder="90"
                                            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-academy-200 focus:border-academy-500 ${examFormErrors.duration ? 'border-danger-500' : 'border-ink-300'
                                                }`}
                                        />
                                        {examFormErrors.duration && <p className="text-danger-500 text-sm mt-1">{examFormErrors.duration}</p>}
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-ink-700 mb-1">
                                            Loại bài thi
                                        </label>
                                        <select
                                            value={examFormData.examType}
                                            onChange={(e) => setExamFormData({ ...examFormData, examType: e.target.value })}
                                            className="min-h-10 w-full rounded-lg border border-ink-300 bg-white px-3 py-2 text-sm text-ink-900 focus:border-academy-500 focus:outline-none focus:ring-2 focus:ring-academy-100"
                                        >
                                            <option value="Kiểm tra">Kiểm tra</option>
                                            <option value="Giữa kỳ">Giữa kỳ</option>
                                            <option value="Cuối kỳ">Cuối kỳ</option>
                                            <option value="Thực hành">Thực hành</option>
                                        </select>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-ink-700 mb-1">
                                            Điểm tối đa *
                                        </label>
                                        <input
                                            type="number"
                                            min="1"
                                            value={examFormData.maxScore}
                                            onChange={(e) => {
                                                setExamFormData({ ...examFormData, maxScore: e.target.value });
                                                if (examFormErrors.maxScore) setExamFormErrors({ ...examFormErrors, maxScore: '' });
                                            }}
                                            placeholder="10"
                                            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-academy-200 focus:border-academy-500 ${examFormErrors.maxScore ? 'border-danger-500' : 'border-ink-300'
                                                }`}
                                        />
                                        {examFormErrors.maxScore && <p className="text-danger-500 text-sm mt-1">{examFormErrors.maxScore}</p>}
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-ink-700 mb-1">
                                            Trạng thái
                                        </label>
                                        <select
                                            value={examFormData.status}
                                            onChange={(e) => setExamFormData({ ...examFormData, status: e.target.value })}
                                            className="min-h-10 w-full rounded-lg border border-ink-300 bg-white px-3 py-2 text-sm text-ink-900 focus:border-academy-500 focus:outline-none focus:ring-2 focus:ring-academy-100"
                                        >
                                            <option value="Sắp thi">Sắp thi</option>
                                            <option value="Đang thi">Đang thi</option>
                                            <option value="Đã hoàn thành">Đã hoàn thành</option>
                                        </select>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-ink-700 mb-1">
                                        Mô tả
                                    </label>
                                    <textarea
                                        value={examFormData.description}
                                        onChange={(e) => setExamFormData({ ...examFormData, description: e.target.value })}
                                        placeholder="Mô tả nội dung bài thi (tùy chọn)"
                                        rows={3}
                                        className="min-h-10 w-full rounded-lg border border-ink-300 bg-white px-3 py-2 text-sm text-ink-900 focus:border-academy-500 focus:outline-none focus:ring-2 focus:ring-academy-100"
                                    />
                                </div>
                            </div>
                </Modal>
            )}

            {/* Delete Exam Confirmation Modal */}
            {showDeleteExamModal && examToDelete && (
                <Modal
                    open={showDeleteExamModal}
                    title="Xác nhận xóa bài thi"
                    onClose={() => {
                            setShowDeleteExamModal(false);
                            setExamToDelete(null);
                        }}
                    className="max-w-md"
                    footer={
                        <div className="flex justify-end space-x-3">
                        <button
                            type="button"
                            onClick={() => {
                                setShowDeleteExamModal(false);
                                setExamToDelete(null);
                            }}
                            className="px-4 py-2 text-ink-700 bg-ink-100 hover:bg-ink-200 rounded-lg transition-colors duration-200"
                            disabled={isDeletingExam}
                        >
                            Hủy
                        </button>
                        <button
                            onClick={handleConfirmDeleteExam}
                            disabled={isDeletingExam}
                            className="px-4 py-2 bg-danger-600 hover:bg-danger-700 text-white rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                        >
                            {isDeletingExam ? (
                                <>
                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                    <span>Đang xóa...</span>
                                </>
                            ) : (
                                <>
                                    <Trash2 className="w-4 h-4" />
                                    <span>Xóa bài thi</span>
                                </>
                            )}
                        </button>
                        </div>
                    }>

                            <div className="space-y-4">
                                <div className="bg-danger-50 p-4 rounded-lg">
                                    <div className="space-y-2">
                                        <p className="text-sm text-danger-800">
                                            <strong>Tên bài thi:</strong> {examToDelete.name}
                                        </p>
                                        <p className="text-sm text-danger-800">
                                            <strong>Loại:</strong> {examToDelete.examType}
                                        </p>
                                        <p className="text-sm text-danger-800">
                                            <strong>Hệ số:</strong> {examToDelete.coefficient}
                                        </p>
                                        <p className="text-sm text-danger-800">
                                            <strong>Ngày thi:</strong> {new Date(examToDelete.examDate).toLocaleDateString('vi-VN')}
                                        </p>
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <p className="text-sm text-ink-600">
                                        Bạn có chắc chắn muốn xóa bài thi này không?
                                    </p>
                                    <div className="bg-warning-50 p-3 rounded-lg">
                                        <p className="text-sm text-warning-800">
                                            <strong>⚠️ Lưu ý:</strong> Việc xóa bài thi sẽ ảnh hưởng đến:
                                        </p>
                                        <ul className="text-sm text-warning-800 mt-1 space-y-1">
                                            <li>• Tất cả điểm số của học sinh trong bài thi này</li>
                                            <li>• Thống kê điểm số của lớp học</li>
                                            <li>• Hành động này không thể hoàn tác</li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                </Modal>
            )}

            {/* Score Management Modal */}
            {showScoresModal && selectedExamForScores && (
                <Modal
                    open={showScoresModal}
                    title={`Quản lý điểm số - ${selectedExamForScores.name}`}
                    description={`${selectedClass.name} • Hệ số: ${selectedExamForScores.coefficient} • Điểm tối đa: ${selectedExamForScores.maxScore}`}
                    onClose={() => {
                            setShowScoresModal(false);
                            setSelectedExamForScores(null);
                            setExamScores([]);
                        }}
                    className="max-w-6xl">
                            {scoresLoading ? (
                                <div className="flex justify-center items-center py-8">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-academy-600"></div>
                                    <span className="ml-3 text-ink-600">Đang tải điểm số...</span>
                                </div>
                            ) : (
                                <div className="space-y-6">
                                    {/* Statistics */}
                                    {examScores.length > 0 && (
                                        <div className="grid grid-cols-2 gap-px overflow-hidden rounded-lg border border-ink-200 bg-ink-200 md:grid-cols-5">
                                            <div className="bg-white px-3 py-2.5">
                                                <div className="text-lg font-semibold tabular-nums text-ink-900">{calculateScoreStats().average}</div>
                                                <div className="text-xs text-ink-500">Điểm TB</div>
                                            </div>
                                            <div className="bg-white px-3 py-2.5">
                                                <div className="text-lg font-semibold tabular-nums text-ink-900">{calculateScoreStats().highest}</div>
                                                <div className="text-xs text-ink-500">Cao nhất</div>
                                            </div>
                                            <div className="bg-white px-3 py-2.5">
                                                <div className="text-lg font-semibold tabular-nums text-ink-900">{calculateScoreStats().lowest}</div>
                                                <div className="text-xs text-ink-500">Thấp nhất</div>
                                            </div>
                                            <div className="bg-white px-3 py-2.5">
                                                <div className="text-lg font-semibold tabular-nums text-ink-900">{calculateScoreStats().scoredStudents}/{calculateScoreStats().totalStudents}</div>
                                                <div className="text-xs text-ink-500">Đã chấm</div>
                                            </div>
                                            <div className="bg-white px-3 py-2.5">
                                                <div className="text-lg font-semibold tabular-nums text-ink-900">{calculateScoreStats().passRate}%</div>
                                                <div className="text-xs text-ink-500">Tỷ lệ đạt</div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Students Score List */}
                                    <div className="space-y-3">
                                        <div className="flex items-center justify-between">
                                            <h4 className="text-md font-semibold text-ink-900">
                                                Điểm số học sinh ({examScores.length})
                                            </h4>
                                            <button
                                                onClick={handleSaveScores}
                                                disabled={isSavingScores}
                                                className="bg-academy-600 hover:bg-academy-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                                            >
                                                {isSavingScores ? (
                                                    <>
                                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                                        <span>Đang lưu...</span>
                                                    </>
                                                ) : (
                                                    <>
                                                        <Award className="w-4 h-4" />
                                                        <span>Lưu điểm số</span>
                                                    </>
                                                )}
                                            </button>
                                        </div>

                                        {examScores.length === 0 ? (
                                            <div className="text-center py-8">
                                                <Award className="w-16 h-16 text-ink-300 mx-auto mb-4" />
                                                <h3 className="text-lg font-medium text-ink-900 mb-2">Không có học sinh nào</h3>
                                                <p className="text-ink-600">Lớp học này chưa có học sinh để chấm điểm.</p>
                                            </div>
                                        ) : (
                                            <div className="space-y-3">
                                                {/* Header */}
                                                <div className="grid grid-cols-12 gap-4 p-3 bg-ink-50 rounded-lg font-medium text-ink-700 text-sm">
                                                    <div className="col-span-4">Học sinh</div>
                                                    <div className="col-span-2">Điểm số</div>
                                                    <div className="col-span-3">Ghi chú</div>
                                                    <div className="col-span-2">Trạng thái</div>
                                                    <div className="col-span-1">Tỷ lệ</div>
                                                </div>

                                                {/* Student Rows */}
                                                {examScores.map((scoreEntry, index) => {
                                                    const score = scoreEntry.score ? parseFloat(scoreEntry.score) : null;
                                                    const percentage = score !== null ? ((score / selectedExamForScores.maxScore) * 100) : null;
                                                    const isValid = scoreEntry.score === null || scoreEntry.score === '' || validateScore(scoreEntry.score.toString(), selectedExamForScores.maxScore);

                                                    return (
                                                        <div key={scoreEntry.studentId} className={`grid grid-cols-12 gap-4 p-3 border rounded-lg ${!isValid ? 'border-danger-300 bg-danger-50' : 'border-ink-200 bg-white'}`}>
                                                            <div className="col-span-4 flex items-center space-x-3">
                                                                <div className="w-8 h-8 bg-ink-100 rounded-full flex items-center justify-center text-ink-700 text-sm font-semibold">
                                                                    {index + 1}
                                                                </div>
                                                                <div>
                                                                    <div className="font-medium text-ink-900">{scoreEntry.studentName}</div>
                                                                    <div className="text-sm text-ink-500">{scoreEntry.studentEmail}</div>
                                                                </div>
                                                            </div>

                                                            <div className="col-span-2">
                                                                <input
                                                                    type="number"
                                                                    step="0.1"
                                                                    min="0"
                                                                    max={selectedExamForScores.maxScore}
                                                                    value={scoreEntry.score || ''}
                                                                    onChange={(e) => handleScoreChange(scoreEntry.studentId, e.target.value, scoreEntry.note)}
                                                                    placeholder={`0-${selectedExamForScores.maxScore}`}
                                                                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-academy-500 focus:border-academy-500 ${!isValid ? 'border-danger-500 bg-danger-50' : 'border-ink-300'
                                                                        }`}
                                                                />
                                                                {!isValid && (
                                                                    <p className="text-danger-500 text-xs mt-1">Điểm không hợp lệ</p>
                                                                )}
                                                            </div>

                                                            <div className="col-span-3">
                                                                <input
                                                                    type="text"
                                                                    value={scoreEntry.note || ''}
                                                                    onChange={(e) => handleScoreChange(scoreEntry.studentId, scoreEntry.score || '', e.target.value)}
                                                                    placeholder="Ghi chú (tùy chọn)"
                                                                    className="w-full px-3 py-2 border border-ink-300 rounded-lg focus:ring-2 focus:ring-academy-500 focus:border-academy-500"
                                                                />
                                                            </div>

                                                            <div className="col-span-2 flex items-center">
                                                                {scoreEntry.score !== null && scoreEntry.score !== '' ? (
                                                                    <span className={`px-2 py-0.5 text-xs rounded ${percentage >= 80 ? 'bg-success-100 text-success-800' :
                                                                        percentage >= 65 ? 'bg-academy-50 text-academy-800' :
                                                                            percentage >= 50 ? 'bg-warning-100 text-warning-800' :
                                                                                'bg-danger-100 text-danger-800'
                                                                        }`}>
                                                                        {percentage >= 50 ? 'Đạt' : 'Không đạt'}
                                                                    </span>
                                                                ) : (
                                                                    <span className="px-2 py-0.5 text-xs bg-ink-100 text-ink-800 rounded">
                                                                        Chưa chấm
                                                                    </span>
                                                                )}
                                                            </div>

                                                            <div className="col-span-1 flex items-center text-sm text-ink-600">
                                                                {percentage !== null ? `${percentage.toFixed(0)}%` : '-'}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>

                                    {/* Quick Actions */}
                                    <div className="border-t border-ink-200 pt-4">
                                        <div className="flex items-center justify-between">
                                            <div className="text-sm text-ink-600">
                                                Mẹo: Bạn có thể nhập điểm từ 0 đến {selectedExamForScores.maxScore}. Để trống nếu chưa chấm điểm.
                                            </div>
                                            <div className="flex space-x-3">
                                                <button
                                                    onClick={() => {
                                                        const newScores = examScores.map(score => ({ ...score, score: null, note: '' }));
                                                        setExamScores(newScores);
                                                    }}
                                                    className="px-3 py-1 text-sm text-ink-600 bg-ink-100 hover:bg-ink-200 rounded transition-colors duration-200"
                                                >
                                                    Xóa tất cả điểm
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        const avgScore = (selectedExamForScores.maxScore * 0.7).toFixed(1);
                                                        const newScores = examScores.map(score => ({ ...score, score: avgScore }));
                                                        setExamScores(newScores);
                                                    }}
                                                    className="px-3 py-1 text-sm text-academy-600 bg-academy-100 hover:bg-academy-200 rounded transition-colors duration-200"
                                                >
                                                    Điền điểm mẫu
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                </Modal>
            )}

            {/* Student Import Modal */}
            {showStudentImportModal && (
                <Modal
                    open={showStudentImportModal}
                    title={`Nhập học sinh vào lớp: ${selectedClass?.name}`}
                    onClose={() => {
                            setShowStudentImportModal(false);
                            resetStudentImport();
                        }}
                    className="max-w-4xl">
                            {/* Import Method Selection */}
                            <div className="mb-6">
                                <label className="block text-sm font-medium text-ink-700 mb-3">
                                    Chọn phương thức nhập
                                </label>
                                <div className="flex space-x-4">
                                    <button
                                        onClick={() => setStudentImportMethod('file')}
                                        className={`flex-1 p-4 rounded-lg border-2 transition-all duration-200 ${studentImportMethod === 'file'
                                            ? 'border-academy-500 bg-academy-50'
                                            : 'border-ink-200 hover:border-ink-300'
                                            }`}
                                    >
                                        <FileSpreadsheet className="w-8 h-8 mx-auto mb-2 text-success-600" />
                                        <p className="font-medium text-ink-900">File Excel</p>
                                        <p className="text-sm text-ink-600">Upload file .xlsx</p>
                                    </button>
                                    <button
                                        onClick={() => setStudentImportMethod('googlesheets')}
                                        className={`flex-1 p-4 rounded-lg border-2 transition-all duration-200 ${studentImportMethod === 'googlesheets'
                                            ? 'border-academy-500 bg-academy-50'
                                            : 'border-ink-200 hover:border-ink-300'
                                            }`}
                                    >
                                        <Globe className="w-8 h-8 mx-auto mb-2 text-academy-600" />
                                        <p className="font-medium text-ink-900">Google Sheets</p>
                                        <p className="text-sm text-ink-600">Nhập link Google Sheets</p>
                                    </button>
                                </div>
                            </div>

                            {/* File Upload */}
                            {studentImportMethod === 'file' && (
                                <div className="mb-6">
                                    <label className="block text-sm font-medium text-ink-700 mb-2">
                                        Upload file Excel
                                    </label>
                                    <div className="border-2 border-dashed border-ink-300 rounded-lg p-6 text-center">
                                        <input
                                            type="file"
                                            accept=".xlsx,.xls"
                                            onChange={handleStudentImportFileUpload}
                                            className="hidden"
                                            id="studentImportFile"
                                        />
                                        <label
                                            htmlFor="studentImportFile"
                                            className="cursor-pointer flex flex-col items-center"
                                        >
                                            <Upload className="w-12 h-12 text-ink-400 mb-4" />
                                            <p className="text-lg font-medium text-ink-900 mb-2">
                                                Chọn file Excel
                                            </p>
                                            <p className="text-sm text-ink-600 mb-4">
                                                Hoặc kéo thả file vào đây
                                            </p>
                                            <div className="bg-academy-600 hover:bg-academy-700 text-white px-4 py-2 rounded-lg transition-colors duration-200 text-sm font-medium">
                                                Chọn file
                                            </div>
                                        </label>
                                    </div>
                                    {studentImportFile && (
                                        <div className="mt-4 flex items-center text-sm text-ink-600">
                                            <FileSpreadsheet className="w-4 h-4 mr-2" />
                                            <span>Đã chọn: {studentImportFile.name}</span>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Google Sheets URL */}
                            {studentImportMethod === 'googlesheets' && (
                                <div className="mb-6">
                                    <label className="block text-sm font-medium text-ink-700 mb-2">
                                        Link Google Sheets (public)
                                    </label>
                                    <div className="flex space-x-3">
                                        <input
                                            type="url"
                                            value={studentImportGoogleSheetsUrl}
                                            onChange={(e) => setStudentImportGoogleSheetsUrl(e.target.value)}
                                            placeholder="https://docs.google.com/spreadsheets/d/..."
                                            className="flex-1 px-3 py-2 border border-ink-300 rounded-lg focus:ring-2 focus:ring-academy-200 focus:border-transparent"
                                        />
                                        <button
                                            onClick={handleStudentImportGoogleSheets}
                                            disabled={!studentImportGoogleSheetsUrl.trim()}
                                            className="bg-academy-600 hover:bg-academy-700 disabled:bg-ink-400 text-white px-4 py-2 rounded-lg transition-colors duration-200 text-sm font-medium"
                                        >
                                            Tải dữ liệu
                                        </button>
                                    </div>
                                    <p className="text-sm text-ink-600 mt-2">
                                        ℹ️ Google Sheets phải được đặt ở chế độ public để có thể truy cập
                                    </p>
                                </div>
                            )}

                            {/* Template Format Info */}
                            <div className="mb-6 bg-academy-50 p-4 rounded-lg">
                                <h4 className="font-medium text-ink-900 mb-2">Định dạng template (giống template học sinh):</h4>
                                <div className="text-sm text-academy-800">
                                    <p className="mb-2">File Excel/Google Sheets cần có các cột sau:</p>
                                    <ul className="list-disc list-inside space-y-1">
                                        <li><strong>Họ và tên</strong> (bắt buộc) - Tên đầy đủ của học sinh</li>
                                        <li><strong>Giới tính</strong> (không bắt buộc) - Nam/Nữ</li>
                                        <li><strong>Ngày sinh</strong> (không bắt buộc) - Định dạng YYYY-MM-DD</li>
                                        <li><strong>Số điện thoại</strong> (không bắt buộc) - Số điện thoại học sinh</li>
                                        <li><strong>Email</strong> (không bắt buộc) - Email liên hệ</li>
                                        <li><strong>Địa chỉ</strong> (không bắt buộc) - Địa chỉ nhà</li>
                                        <li><strong>Tên phụ huynh</strong> (không bắt buộc) - Tên người bảo hộ</li>
                                        <li><strong>SĐT phụ huynh</strong> (không bắt buộc) - Số điện thoại phụ huynh</li>
                                    </ul>
                                    <p className="text-xs text-academy-600 mt-2">
                                        ⚠️ Chỉ nhập những học sinh đã tồn tại trong hệ thống. Khi có nhiều học sinh cùng tên, hệ thống sẽ dùng email, số điện thoại và thông tin khác để đối chiếu chính xác.
                                    </p>
                                </div>
                            </div>

                            {/* Preview Data */}
                            {studentImportData.length > 0 && (
                                <div className="mb-6">
                                    <h4 className="font-medium text-ink-900 mb-3">
                                        📊 Xem trước dữ liệu ({studentImportData.length} học sinh)
                                    </h4>
                                    <div className="border border-ink-200 rounded-lg overflow-hidden max-h-60 overflow-y-auto">
                                        <table className="w-full text-sm">
                                            <thead>
                                                <tr>
                                                    <th className="px-2 py-2 text-left text-xs font-medium text-ink-500">STT</th>
                                                    <th className="px-2 py-2 text-left text-xs font-medium text-ink-500">Họ và tên</th>
                                                    <th className="px-2 py-2 text-left text-xs font-medium text-ink-500">Giới tính</th>
                                                    <th className="px-2 py-2 text-left text-xs font-medium text-ink-500">Ngày sinh</th>
                                                    <th className="px-2 py-2 text-left text-xs font-medium text-ink-500">SĐT</th>
                                                    <th className="px-2 py-2 text-left text-xs font-medium text-ink-500">Email</th>
                                                    <th className="px-2 py-2 text-left text-xs font-medium text-ink-500">SĐT PH</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-ink-200">
                                                {studentImportData.slice(0, 10).map((student, index) => (
                                                    <tr key={index}>
                                                        <td className="px-2 py-2 text-sm text-ink-900">{index + 1}</td>
                                                        <td className="px-2 py-2 text-sm text-ink-900 font-medium">{student.fullName}</td>
                                                        <td className="px-2 py-2 text-sm text-ink-500">{student.gender || '-'}</td>
                                                        <td className="px-2 py-2 text-sm text-ink-500">{student.dateOfBirth || '-'}</td>
                                                        <td className="px-2 py-2 text-sm text-ink-500">{student.phone || '-'}</td>
                                                        <td className="px-2 py-2 text-sm text-ink-500">{student.email || '-'}</td>
                                                        <td className="px-2 py-2 text-sm text-ink-500">{student.parentPhone || '-'}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                        {studentImportData.length > 10 && (
                                            <div className="bg-ink-50 px-4 py-2 text-sm text-ink-600 text-center">
                                                ... và {studentImportData.length - 10} học sinh khác
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Import Results */}
                            {studentImportResults && (
                                <div className="mb-6">
                                    <h4 className="font-medium text-ink-900 mb-3">📈 Kết quả nhập</h4>
                                    <div className="space-y-4">
                                        {/* Success Results */}
                                        {studentImportResults.success.length > 0 && (
                                            <div className="bg-success-50 border border-success-200 rounded-lg p-4">
                                                <h5 className="font-medium text-success-800 mb-2">
                                                    ✅ Thành công ({studentImportResults.success.length})
                                                </h5>
                                                <div className="text-sm text-success-700">
                                                    {studentImportResults.success.slice(0, 3).map((item: any, index: number) => (
                                                        <p key={index}>• {item.data.fullName}</p>
                                                    ))}
                                                    {studentImportResults.success.length > 3 && (
                                                        <p>... và {studentImportResults.success.length - 3} học sinh khác</p>
                                                    )}
                                                </div>
                                            </div>
                                        )}

                                        {/* Error Results */}
                                        {studentImportResults.errors.length > 0 && (
                                            <div className="bg-danger-50 border border-danger-200 rounded-lg p-4">
                                                <h5 className="font-medium text-danger-800 mb-2">
                                                    ❌ Lỗi ({studentImportResults.errors.length})
                                                </h5>
                                                <div className="text-sm text-danger-700 max-h-40 overflow-y-auto">
                                                    {studentImportResults.errors.map((item: any, index: number) => (
                                                        <p key={index} className="mb-1">
                                                            • Dòng {item.row}: {item.data.fullName} - {item.error}
                                                        </p>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Action Buttons */}
                            <div className="flex justify-end space-x-3 pt-4 border-t border-ink-200">
                                <button
                                    onClick={() => {
                                        setShowStudentImportModal(false);
                                        resetStudentImport();
                                    }}
                                    disabled={isImportingStudents}
                                    className="px-4 py-2 text-ink-700 bg-ink-200 hover:bg-ink-300 rounded-lg transition-colors duration-200 disabled:opacity-50"
                                >
                                    Đóng
                                </button>
                                {studentImportData.length > 0 && !studentImportResults && (
                                    <button
                                        onClick={handleImportStudentsToClass}
                                        disabled={isImportingStudents}
                                        className="px-6 py-2 bg-academy-600 hover:bg-academy-700 disabled:bg-ink-400 text-white rounded-lg transition-colors duration-200 flex items-center space-x-2 text-sm font-medium"
                                    >
                                        {isImportingStudents ? (
                                            <>
                                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                                <span>Đang nhập...</span>
                                            </>
                                        ) : (
                                            <>
                                                <Upload className="w-4 h-4" />
                                                <span>Nhập {studentImportData.length} học sinh</span>
                                            </>
                                        )}
                                    </button>
                                )}
                            </div>
                </Modal>
            )}

            {/* Click outside to close dropdowns */}
            {openDropdown !== null && (
                <div
                    className="fixed inset-0 z-40"
                    onClick={() => setOpenDropdown(null)}
                />
            )}
        </div>
    );
};

export default Classes;
